<template>
  <div class="min-h-screen bg-gray-50 p-3">
    <div class="max-w-6xl mx-auto">
      <!-- Page Header -->
      <div class="mb-4">
        <div class="relative rounded-2xl p-4 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden w-full" 
             style="background: linear-gradient(135deg, #10B981 0%, #059669 25%, #047857 50%, #065F46 75%, #064E3B 100%);">
          
          <!-- Background decorative elements -->
          <div class="absolute inset-0 overflow-hidden">
            <div class="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-20"
                 style="background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%);"></div>
          </div>
          
          <div class="relative z-10">
            <div class="flex items-center space-x-4 mb-3">
              <div class="relative flex-shrink-0">
                <div class="w-16 h-16 rounded-full flex items-center justify-center border-3 border-white/70 shadow-xl" 
                     style="background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%);">
                  <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h2m5 0h2a2 2 0 002-2V7a2 2 0 00-2-2h-2m-5 4v2m0 0v2m0-2h2m-2 0H9"></path>
            </svg>
          </div>
              </div>
              
          <div>
                <h1 class="text-2xl font-bold text-white">{{ directT('settings.leadsSettings.title') }}</h1>
                <p class="text-white/90 text-lg font-medium">{{ directT('settings.leadsSettings.subtitle') }}</p>
          </div>
        </div>
          </div>
        </div>
        
        <div class="mt-4">
        <SettingsNav />
        </div>
      </div>
      
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <!-- Lead Status Settings -->
        <div class="bg-white rounded-2xl border border-gray-200 p-4 shadow-lg hover:shadow-xl transition-shadow duration-300">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg flex items-center justify-center" style="background-color: #059669;">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
              </div>
              <h3 class="text-base font-semibold text-gray-900">{{ directT('settings.leadsSettings.statusManagement.title') }}</h3>
            </div>
          </div>
          
          <div class="mb-3">
            <div v-if="statusList.length === 0" class="text-center py-6">
              <div class="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <h4 class="text-sm font-medium text-slate-900 dark:text-white mb-1">{{ directT('settings.leadsSettings.statusManagement.noStatuses') }}</h4>
              <p class="text-slate-500 text-xs">{{ directT('settings.leadsSettings.statusManagement.noStatusesDescription') }}</p>
            </div>
            
            <!-- Status List -->
            <div v-else class="space-y-1.5">
                <div 
                  v-for="(status, index) in statusList" 
                  :key="status.id"
                class="group bg-slate-50 dark:bg-slate-700/50 rounded-md border border-slate-200 dark:border-slate-600 p-2.5 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2.5">
                    <div class="w-4 h-4 rounded-full" :style="{ backgroundColor: status.color }"></div>
                    <span class="text-sm font-medium text-slate-900 dark:text-white">{{ status.name }}</span>
                  </div>
                  <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button 
                        @click="editStatus(index)" 
                      class="w-6 h-6 rounded bg-slate-200 hover:bg-slate-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-600 dark:text-slate-300 transition-all duration-200 flex items-center justify-center"
                      >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                      </button>
                      <button 
                        @click="deleteStatus(index)" 
                      class="w-6 h-6 rounded bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 transition-all duration-200 flex items-center justify-center"
                      >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
              </div>
            </div>
          </div>
          
          <div class="flex gap-2 pt-4 border-t border-gray-200">
            <button 
              @click="addNewStatus" 
              class="flex-1 px-4 py-2 text-white rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              style="background: linear-gradient(135deg, #10B981 0%, #059669 100%);"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              {{ directT('settings.leadsSettings.statusManagement.addStatus') }}
            </button>
            
            <!-- Auto-save status indicator -->
            <div v-if="saving" class="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg text-sm">
              <svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              {{ directT('settings.leadsSettings.autoSaving') }}
            </div>
          </div>
        </div>
        
        <!-- Lead Source Settings -->
        <div class="bg-white rounded-2xl border border-gray-200 p-4 shadow-lg hover:shadow-xl transition-shadow duration-300">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg flex items-center justify-center" style="background-color: #3B82F6;">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
              </svg>
              </div>
              <h3 class="text-base font-semibold text-gray-900">{{ directT('settings.leadsSettings.sourceManagement.title') }}</h3>
            </div>
          </div>
          
          <div class="mb-3">
            <div v-if="sourceList.length === 0" class="text-center py-6">
              <div class="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                </svg>
              </div>
              <h4 class="text-sm font-medium text-slate-900 dark:text-white mb-1">{{ directT('settings.leadsSettings.sourceManagement.noSources') }}</h4>
              <p class="text-slate-500 text-xs">{{ directT('settings.leadsSettings.sourceManagement.noSourcesDescription') }}</p>
              </div>

              <!-- Sources List -->
            <div v-else class="space-y-1.5">
                <div 
                  v-for="(source, index) in sourceList" 
                  :key="source.id"
                class="group bg-slate-50 dark:bg-slate-700/50 rounded-md border border-slate-200 dark:border-slate-600 p-2.5 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200"
                >
                  <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2.5">
                    <div class="w-4 h-4 bg-emerald-100 dark:bg-emerald-900/30 rounded flex items-center justify-center">
                      <svg class="w-2.5 h-2.5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                        </svg>
                      </div>
                    <span class="text-sm font-medium text-slate-900 dark:text-white">{{ source.name }}</span>
                      </div>
                  <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button 
                        @click="editSource(index)" 
                      class="w-6 h-6 rounded bg-slate-200 hover:bg-slate-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-600 dark:text-slate-300 transition-all duration-200 flex items-center justify-center"
                      >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                      </button>
                      <button 
                        @click="deleteSource(index)" 
                      class="w-6 h-6 rounded bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 transition-all duration-200 flex items-center justify-center"
                      >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="flex gap-2 pt-4 border-t border-gray-200">
            <button 
              @click="addNewSource" 
              class="flex-1 px-4 py-2 text-white rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              style="background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              {{ directT('settings.leadsSettings.sourceManagement.addSource') }}
            </button>
            
            <!-- Auto-save status indicator -->
            <div v-if="saving" class="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm">
              <svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              {{ directT('settings.leadsSettings.autoSaving') }}
            </div>
          </div>
        </div>
        
        <!-- Lead Form Designer -->
        <div class="lg:col-span-2">
          <div class="bg-white rounded-2xl border border-gray-200 p-4 shadow-lg hover:shadow-xl transition-shadow duration-300">
            
            <!-- Form Cards -->
            <div class="mb-6">
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center" style="background: linear-gradient(135deg, #10B981, #059669);">
                    <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
                  <div>
                    <h4 class="text-sm font-semibold text-gray-900">{{ directT('settings.leadsSettings.existingCards.title') }}</h4>
                    <p class="text-xs text-gray-600">{{ directT('settings.leadsSettings.existingCards.description') }}</p>
              </div>
            </div>
                <button 
                  @click="addNewCard" 
                  class="px-4 py-2 text-white text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105"
                  style="background: linear-gradient(135deg, #10B981, #059669);"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  {{ directT('settings.leadsSettings.existingCards.addNewCard') }}
                </button>
                </div>
              
              <!-- Default Card - Compact View -->
              <div class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 mb-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                        </svg>
                    </div>
                    <div>
                      <h5 class="text-sm font-medium text-gray-900">{{ directT('settings.leadsSettings.existingCards.basicLeadInfo.title') }}</h5>
                      <p class="text-xs text-gray-500">{{ directT('settings.leadsSettings.existingCards.basicLeadInfo.description') }}</p>
                  </div>
                </div>
                  <div class="flex items-center gap-2">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-600">
                      {{ directT('settings.leadsSettings.existingCards.basicLeadInfo.active') }}
                    </span>
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                      {{ directT('settings.leadsSettings.existingCards.basicLeadInfo.builtIn') }}
                    </span>
                    </div>
                  </div>
                  
                <!-- Included Fields -->
                <div class="mt-3 pt-3 border-t border-gray-100">
                  <div class="text-xs font-medium text-gray-700 mb-2">{{ directT('settings.leadsSettings.existingCards.basicLeadInfo.includedFields') }}</div>
                  <div class="flex flex-wrap gap-1">
                    <span class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{{ directT('settings.leadsSettings.existingCards.basicLeadInfo.fullName') }}</span>
                    <span class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{{ directT('settings.leadsSettings.existingCards.basicLeadInfo.email') }}</span>
                    <span class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{{ directT('settings.leadsSettings.existingCards.basicLeadInfo.phone') }}</span>
                    <span class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{{ directT('settings.leadsSettings.existingCards.basicLeadInfo.company') }}</span>
                    <span class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{{ directT('settings.leadsSettings.existingCards.basicLeadInfo.status') }}</span>
                    <span class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{{ directT('settings.leadsSettings.existingCards.basicLeadInfo.source') }}</span>
                        </div>
                </div>
                        </div>
                      </div>
                  
            <!-- Custom Cards Section -->
              
              <div v-if="systemAreas.length === 0" class="text-center py-8">
                <div class="mx-auto w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center mb-3">
                  <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                    </div>
                <h4 class="text-sm font-medium text-slate-900 dark:text-white mb-1">{{ directT('settings.leadsSettings.existingCards.noAdditionalCards') }}</h4>
                <p class="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">{{ directT('settings.leadsSettings.existingCards.createCustomCards') }}</p>
            </div>
            
              <!-- Custom Cards Display -->
              <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                <div v-for="(area, areaIndex) in systemAreas" :key="area.id" 
                     class="bg-slate-50 dark:bg-slate-700/50 rounded-md border border-slate-200 dark:border-slate-600 p-3 hover:border-slate-300 dark:hover:border-slate-500 transition-colors">
                  
                  <!-- Card Header -->
                  <div class="flex items-start justify-between mb-2">
                    <div class="flex-1 min-w-0">
                      <h3 class="text-xs font-medium text-slate-900 dark:text-white truncate">{{ area.title }}</h3>
                      <p class="text-xs text-slate-500 dark:text-slate-400">{{ area.fields.length }} fields</p>
                    </div>
                    <div class="flex items-center gap-0.5">
                      <button @click="editArea(areaIndex)" 
                              class="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600 rounded transition-colors">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>
              
                      <button @click="deleteArea(areaIndex)" 
                              class="p-1 text-slate-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
                  </div>

                  <!-- Fields Preview -->
                  <div class="space-y-1">
                    <div v-if="area.fields.length > 0">
                      <div v-for="field in area.fields.slice(0, 2)" :key="field.id" 
                           class="flex items-center gap-1.5 text-xs">
                        <div class="w-1.5 h-1.5 rounded-full bg-slate-400"></div>
                        <span class="text-slate-600 dark:text-slate-400 truncate flex-1">{{ field.label }}</span>
                        <span class="text-slate-400 text-xs shrink-0">{{ getFieldTypeLabel(field.type) }}</span>
                      </div>
                      <div v-if="area.fields.length > 2" class="text-xs text-slate-400 ml-3">
                        +{{ area.fields.length - 2 }} more
          </div>
        </div>
        
                    <div v-else class="text-xs text-slate-400 italic">
                      No fields configured
              </div>
            </div>
          </div>
        </div>
          </div>
        </div>
        

      </div>
    </div>
    
    <!-- Status Edit Modal -->
    <div v-if="showStatusModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/30 backdrop-blur-md" @click="closeStatusModal"></div>
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 z-10 relative border border-gray-200 max-h-[85vh] overflow-hidden">
        
        <!-- Header with gradient -->
        <div class="px-6 py-4" style="background: linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%);">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center border border-white/20">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-white">{{ editingStatusIndex === -1 ? directT('settings.leadsSettings.statusManagement.addStatusTitle') : directT('settings.leadsSettings.statusManagement.editStatusTitle') }}</h3>
                <p class="text-white/90 text-sm">{{ editingStatusIndex === -1 ? 'Create a new lead status' : 'Update lead status settings' }}</p>
              </div>
            </div>
            <button @click="closeStatusModal" class="w-8 h-8 bg-white/30 hover:bg-white/40 rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-6">
          <div class="text-center">
            <div class="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
              <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <h4 class="text-lg font-semibold text-gray-900 mb-2">Lead Status Configuration</h4>
            <p class="text-gray-600 text-sm">Configure the status name and visual appearance for your leads</p>
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.leadsSettings.statusManagement.statusName') }}</label>
              <input
                v-model="editingStatus.name"
                type="text"
                class="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-100 focus:border-green-500 bg-gray-50 text-gray-900 transition-all duration-300 placeholder:text-gray-400"
                :placeholder="directT('settings.leadsSettings.statusManagement.statusNamePlaceholder')"
              />
            </div>
            
            <div>
              <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.leadsSettings.statusManagement.statusColor') }}</label>
              <div class="flex items-center gap-3">
                <input
                  v-model="editingStatus.color"
                  type="color"
                  class="w-16 h-12 rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all duration-300"
                />
                <div class="flex-1">
                  <div class="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div class="w-6 h-6 rounded-full border-2 border-white shadow-sm" :style="{ backgroundColor: editingStatus.color }"></div>
                    <div>
                      <p class="text-sm font-medium text-gray-900">{{ editingStatus.name || 'Status Preview' }}</p>
                      <p class="text-xs text-gray-500">{{ editingStatus.color }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div class="text-xs text-gray-500">
            <span class="font-medium">Tip:</span> Choose a distinctive color for easy identification
          </div>
          <div class="flex gap-2">
            <button
              @click="closeStatusModal"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm font-medium"
            >
              {{ directT('settings.leadsSettings.cancel') }}
            </button>
            <button
              @click="saveStatus"
              :disabled="!editingStatus.name.trim()"
              class="px-6 py-2 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-md disabled:shadow-none"
              style="background: linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%);"
            >
              {{ directT('settings.leadsSettings.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Source Edit Modal -->
    <div v-if="showSourceModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/30 backdrop-blur-md" @click="closeSourceModal"></div>
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 z-10 relative border border-gray-200 max-h-[85vh] overflow-hidden">
        
        <!-- Header with gradient -->
        <div class="px-6 py-4" style="background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%);">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center border border-white/20">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-white">{{ editingSourceIndex === -1 ? directT('settings.leadsSettings.sourceManagement.addSourceTitle') : directT('settings.leadsSettings.sourceManagement.editSourceTitle') }}</h3>
                <p class="text-white/90 text-sm">{{ editingSourceIndex === -1 ? 'Create a new lead source' : 'Update lead source settings' }}</p>
              </div>
            </div>
            <button @click="closeSourceModal" class="w-8 h-8 bg-white/30 hover:bg-white/40 rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-6">
          <div class="text-center">
            <div class="w-16 h-16 bg-gradient-to-br from-blue-100 to-sky-100 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
              <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
              </svg>
            </div>
            <h4 class="text-lg font-semibold text-gray-900 mb-2">Lead Source Configuration</h4>
            <p class="text-gray-600 text-sm">Configure the source name for tracking where your leads come from</p>
          </div>

          <form @submit.prevent="saveSource" class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.leadsSettings.sourceManagement.sourceName') }}</label>
              <input
                type="text"
                v-model="editingSource.name"
                required
                class="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-gray-50 text-gray-900 transition-all duration-300 placeholder:text-gray-400"
                :placeholder="directT('settings.leadsSettings.sourceManagement.sourceNamePlaceholder')"
              />
            </div>

            <!-- Preview Box -->
            <div class="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div class="flex items-center gap-2">
                <div class="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                  <svg class="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ editingSource.name || 'Source Preview' }}</p>
                  <p class="text-xs text-gray-500">Lead Source</p>
                </div>
              </div>
            </div>
            
            <!-- Footer -->
            <div class="pt-4 flex items-center justify-between">
              <div class="text-xs text-gray-500">
                <span class="font-medium">Tip:</span> Use descriptive names like "Website", "Social Media", etc.
              </div>
              <div class="flex gap-2">
                <button
                  type="button"
                  @click="closeSourceModal"
                  class="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm font-medium"
                >
                  {{ directT('settings.leadsSettings.cancel') }}
                </button>
                <button
                  type="submit"
                  :disabled="!editingSource.name.trim()"
                  class="px-6 py-2 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-md disabled:shadow-none"
                  style="background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%);"
                >
                  {{ directT('settings.leadsSettings.save') }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/30 backdrop-blur-md" @click="closeDeleteModal"></div>
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 z-10 relative border border-gray-200 max-h-[85vh] overflow-hidden">
        
        <!-- Header with gradient -->
        <div class="px-6 py-4" style="background: linear-gradient(135deg, #DC2626 0%, #B91C1C 50%, #991B1B 100%);">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center border border-white/20">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-white">{{ directT('settings.leadsSettings.confirmDeletion') }}</h3>
                <p class="text-white/90 text-sm">This action cannot be undone</p>
              </div>
            </div>
            <button @click="closeDeleteModal" class="w-8 h-8 bg-white/30 hover:bg-white/40 rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-6">
          <div class="text-center">
            <div class="w-16 h-16 bg-gradient-to-br from-red-100 to-rose-100 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </div>
            <h4 class="text-lg font-semibold text-gray-900 mb-2">Delete {{ deleteType === 'status' ? 'Status' : 'Source' }}</h4>
            <p class="text-gray-600 text-sm">{{ deleteType === 'status' ? directT('settings.leadsSettings.statusManagement.deleteConfirmation') : directT('settings.leadsSettings.sourceManagement.deleteConfirmation') }}</p>
          </div>

          <!-- Warning Box -->
          <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div class="flex items-start gap-3">
              <div class="w-6 h-6 bg-red-100 rounded-lg flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <div class="text-red-800">
                <p class="font-semibold text-sm mb-1">Warning</p>
                <p class="text-sm text-red-700">This will permanently remove the {{ deleteType }} from your system. Any leads with this {{ deleteType }} will need to be reassigned.</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div class="text-xs text-gray-500">
            <span class="font-medium">Note:</span> This action is permanent and cannot be reversed
          </div>
          <div class="flex gap-2">
            <button
              @click="closeDeleteModal"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm font-medium"
            >
              {{ directT('settings.leadsSettings.cancel') }}
            </button>
            <button
              @click="confirmDelete"
              class="px-6 py-2 text-white rounded-lg hover:shadow-lg transition-all duration-200 text-sm font-medium shadow-md"
              style="background: linear-gradient(135deg, #DC2626 0%, #B91C1C 50%, #991B1B 100%);"
            >
              {{ directT('settings.leadsSettings.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Card Creation Modal -->
    <div v-if="isAddingCard" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/30 backdrop-blur-md" @click="cancelCardCreation"></div>
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 z-10 relative border border-gray-200 max-h-[80vh] overflow-hidden">
        
        <!-- Step 1: Card Name -->
        <div v-if="currentStep === 1" class="flex flex-col h-full">
          <!-- Header with gradient -->
          <div class="px-6 py-4" style="background: linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%);">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center border border-white/20">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-white">{{ directT('settings.leadsSettings.cardCreation.title') }}</h3>
                  <p class="text-white/90 text-sm">{{ directT('settings.leadsSettings.cardCreation.step1Of2') }}</p>
                </div>
              </div>
              <button @click="cancelCardCreation" class="w-8 h-8 bg-white/30 hover:bg-white/40 rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 p-6">
            <div class="max-w-md mx-auto">
              <div class="text-center mb-6">
                <div class="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md">
                  <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                </div>
                <h4 class="text-lg font-semibold text-gray-900 mb-2">{{ directT('settings.leadsSettings.cardCreation.cardInformation') }}</h4>
                <p class="text-gray-600">{{ directT('settings.leadsSettings.cardCreation.cardInformationDescription') }}</p>
              </div>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.leadsSettings.cardCreation.cardName') }}</label>
                  <input
                    v-model="editingArea.title"
                    type="text"
                    :placeholder="directT('settings.leadsSettings.cardCreation.cardNamePlaceholder')"
                    class="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-100 focus:border-green-500 bg-gray-50 text-gray-900 transition-all duration-300 placeholder:text-gray-400"
                  />
                </div>
                
                <!-- Preview Card -->
                <div class="mt-6 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                  <div class="flex items-center gap-2 mb-2">
                    <div class="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                      </svg>
                    </div>
                    <div>
                      <h5 class="text-sm font-medium text-gray-900">{{ editingArea.title || directT('settings.leadsSettings.cardCreation.cardNamePreview') }}</h5>
                      <p class="text-xs text-gray-500">{{ directT('settings.leadsSettings.cardCreation.customCard') }}</p>
                    </div>
                  </div>
                  <p class="text-xs text-gray-500">{{ directT('settings.leadsSettings.cardCreation.previewDescription') }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div class="text-xs text-gray-500">
              {{ directT('settings.leadsSettings.cardCreation.nextStep') }}
            </div>
            <div class="flex gap-2">
              <button
                @click="cancelCardCreation"
                class="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm font-medium"
              >
                {{ directT('settings.leadsSettings.cardCreation.cancel') }}
              </button>
              <button
                @click="proceedToFieldSelection"
                :disabled="!editingArea.title.trim()"
                class="px-6 py-2 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-md disabled:shadow-none"
                style="background: linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%);"
              >
                {{ directT('settings.leadsSettings.cardCreation.continue') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Step 2: Field Selection -->
        <div v-if="currentStep === 2" class="flex h-[80vh]">
          <!-- Left Side: Field Types -->
          <div class="w-1/2 border-r border-gray-200 flex flex-col bg-gray-50">
            <!-- Header -->
            <div class="px-4 py-3" style="background: linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%);">
              <div class="flex items-center gap-2 mb-1">
                <div class="w-6 h-6 bg-white/30 rounded flex items-center justify-center border border-white/20">
                  <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                </div>
                <h4 class="text-sm font-semibold text-white">{{ directT('settings.leadsSettings.cardCreation.addFields') }}</h4>
              </div>
              <p class="text-white/90 text-xs">{{ directT('settings.leadsSettings.cardCreation.addFieldsDescription') }}</p>
            </div>
            
            <div class="flex-1 p-3 overflow-y-auto">
              <div class="grid grid-cols-1 gap-2">
                <button
                  v-for="fieldType in fieldTypes"
                  :key="fieldType.type"
                  @click="selectFieldType(fieldType.type)"
                  class="group p-3 text-left bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all duration-200"
                >
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 bg-blue-100 group-hover:bg-blue-200 rounded flex items-center justify-center transition-colors duration-200">
                      <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getFieldIcon(fieldType.type)"></path>
                      </svg>
                    </div>
                    <div>
                      <span class="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-200">{{ fieldType.name }}</span>
                      <p class="text-xs text-gray-500">{{ directT('settings.leadsSettings.cardCreation.addThisFieldType') }}</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Right Side: Card Preview -->
          <div class="w-1/2 flex flex-col bg-white">
            <!-- Header -->
            <div class="px-4 py-3" style="background: linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%);">
              <div class="flex items-center justify-between">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <div class="w-6 h-6 bg-white/30 rounded flex items-center justify-center border border-white/20">
                      <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                      </svg>
                    </div>
                    <h4 class="text-sm font-semibold text-white">{{ editingArea.title }}</h4>
                  </div>
                  <p class="text-white/90 text-xs">{{ editingArea.fields.length }} {{ directT('settings.leadsSettings.cardCreation.fieldsConfigured') }}</p>
                </div>
                                  <button
                  @click="finishCardCreation"
                  :disabled="editingArea.fields.length === 0"
                  class="px-3 py-1.5 text-xs font-medium text-green-600 bg-white rounded hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm disabled:shadow-none"
                >
                  {{ directT('settings.leadsSettings.cardCreation.saveCard') }}
                </button>
              </div>
            </div>
            
            <div class="flex-1 p-3 overflow-y-auto">
              <div v-if="editingArea.fields.length === 0" class="text-center py-8">
                <div class="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                </div>
                <h5 class="text-sm font-medium text-gray-900 mb-1">{{ directT('settings.leadsSettings.cardCreation.noFieldsYet') }}</h5>
                <p class="text-xs text-gray-500">{{ directT('settings.leadsSettings.cardCreation.chooseFieldTypes') }}</p>
              </div>
              
              <div v-else class="space-y-2">
                <div
                  v-for="(field, index) in editingArea.fields"
                  :key="field.id || index"
                  class="group p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                >
                  <div class="flex items-start justify-between">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <div class="w-6 h-6 bg-green-100 rounded flex items-center justify-center">
                          <svg class="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                        </div>
                        <div class="flex-1">
                          <div class="flex items-center gap-2">
                            <span class="text-sm font-medium text-gray-900">{{ field.label }}</span>
                            <span v-if="field.required" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{{ directT('settings.leadsSettings.cardCreation.required') }}</span>
                          </div>
                          <div class="flex items-center gap-2 mt-0.5">
                            <span class="text-xs text-gray-600">{{ getFieldTypeLabel(field.type) }}</span>
                            <span class="text-gray-400">•</span>
                            <span class="text-xs text-gray-500 truncate">{{ field.placeholder }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        @click="editFieldInCard(index)"
                        class="w-6 h-6 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded flex items-center justify-center transition-all duration-200"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                      </button>
                      <button
                        @click="removeFieldFromCard(field.id)"
                        class="w-6 h-6 bg-red-100 hover:bg-red-200 text-red-600 rounded flex items-center justify-center transition-all duration-200"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Footer -->
            <div class="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <button
                @click="currentStep = 1"
                class="flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:text-gray-800 transition-colors duration-200 text-sm"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                {{ directT('settings.leadsSettings.cardCreation.backToCardInfo') }}
              </button>
              <div class="text-xs text-gray-500">
                <span class="font-medium">{{ editingArea.fields.length }}</span> {{ directT('settings.leadsSettings.cardCreation.fieldsConfigured') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Field Type Selection Modal with Card Preview (old version) -->
    <div v-if="showFieldTypeModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/20 backdrop-blur-sm" @click="cancelCardCreation"></div>
      <div class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-6xl mx-4 z-10 relative max-h-[85vh] overflow-hidden border border-slate-200 dark:border-slate-700">
        
        <!-- Header -->
        <div class="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-sm font-semibold text-slate-900 dark:text-white">Building Card: {{ editingArea.title }}</h3>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Add fields to your custom card</p>
            </div>
          <button 
              @click="cancelCardCreation" 
              class="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
          </div>
        </div>
        
        <div class="flex h-[calc(85vh-80px)]">
          <!-- Left Side: Field Types -->
          <div class="flex-1 p-4 overflow-y-auto">
            <h4 class="text-sm font-medium text-slate-900 dark:text-white mb-3">Choose Field Type</h4>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div 
                v-for="fieldType in fieldTypes" 
                :key="fieldType.type"
                @click="selectFieldType(fieldType.type)"
                class="group relative p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 cursor-pointer transition-all duration-200 hover:shadow-sm"
              >
                <!-- Field Icon -->
                <div class="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm mb-2 group-hover:scale-105 transition-all duration-200"
                     :style="{ background: getFieldTypeIconColors(fieldType.type).background }">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                       :style="{ color: getFieldTypeIconColors(fieldType.type).icon }">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="getFieldIcon(fieldType.type)"></path>
                  </svg>
                </div>

                <!-- Field Info -->
                <div>
                  <h5 class="text-xs font-medium text-slate-900 dark:text-white mb-1">{{ fieldType.name }}</h5>
                  <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{{ fieldType.description }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Side: Card Preview -->
          <div class="w-80 border-l border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-medium text-slate-900 dark:text-white">Card Preview</h4>
              <span class="text-xs text-slate-500 dark:text-slate-400">{{ editingArea.fields.length }} fields</span>
            </div>
            
            <!-- Card Preview -->
            <div class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 mb-4">
              <div class="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
                <div class="w-5 h-5 rounded bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                  <svg class="w-3 h-3 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                </div>
                <h5 class="text-sm font-medium text-slate-900 dark:text-white">{{ editingArea.title }}</h5>
              </div>
              
              <div v-if="editingArea.fields.length === 0" class="text-center py-6">
                <div class="w-8 h-8 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                </div>
                <p class="text-xs text-slate-500 dark:text-slate-400">No fields added yet</p>
              </div>
              
              <div v-else class="space-y-2">
                <div v-for="field in editingArea.fields" :key="field.id" 
                     class="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded border border-slate-200 dark:border-slate-600">
                  <div class="w-4 h-4 rounded flex items-center justify-center"
                       :style="{ background: getFieldTypeIconColors(field.type).background }">
                    <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         :style="{ color: getFieldTypeIconColors(field.type).icon }">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getFieldIcon(field.type)"></path>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-medium text-slate-900 dark:text-white truncate">{{ field.label }}</p>
                    <p class="text-xs text-slate-500 dark:text-slate-400">{{ getFieldTypeLabel(field.type) }}</p>
                  </div>
                  <button 
                    @click="removeFieldFromCard(field.id)"
                    class="p-0.5 text-slate-400 hover:text-red-500 rounded transition-colors"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Action Button -->
            <button 
              @click="finishCardCreation"
              class="w-full px-3 py-2 text-white rounded text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 hover:shadow-md"
              style="background: linear-gradient(135deg, #10B981, #059669);"
              :disabled="editingArea.fields.length === 0"
              :class="editingArea.fields.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              Finish Card ({{ editingArea.fields.length }} fields)
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Field Details Modal -->
    <div v-if="showFieldDetailsModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/20 backdrop-blur-sm" @click="showFieldDetailsModal = false"></div>
      <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-2xl mx-4 z-10 relative max-h-[85vh] overflow-hidden">
        
        <!-- Header -->
        <div class="relative overflow-hidden">
          <div class="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900/20 dark:to-indigo-900/20"></div>
          <div class="relative px-4 py-3 border-b border-slate-200/50 dark:border-slate-700/50">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <!-- Back Button -->
                <button 
                  @click="() => { showFieldDetailsModal = false; showFieldTypeModal = true; }"
                  class="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-white/80 dark:hover:bg-slate-800/80 rounded-lg transition-all duration-200"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                </button>
                
                <div class="w-8 h-8 rounded-lg shadow-sm flex items-center justify-center"
                     :style="{ background: getFieldTypeIconColors(editingField.type).background }">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                       :style="{ color: getFieldTypeIconColors(editingField.type).icon }">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getFieldIcon(editingField.type)"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-slate-900 dark:text-white">{{ getFieldTypeLabel(editingField.type) }} Field</h3>
                  <p class="text-xs text-slate-500 dark:text-slate-400">Configure field settings</p>
                </div>
              </div>
              <button 
                @click="showFieldDetailsModal = false" 
                class="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white/80 dark:hover:bg-slate-800/80 rounded-lg transition-all duration-200"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="p-4 overflow-y-auto max-h-[calc(85vh-120px)] bg-slate-50/30 dark:bg-slate-900/30">
          <form @submit.prevent="saveFieldDetails" class="space-y-4">
            
            <!-- Field Label - Universal for all types -->
            <div class="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <span class="text-rose-500">*</span> Field Label
              </label>
          <input 
            type="text" 
                v-model="editingField.label" 
            required
                class="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-900 dark:text-white transition-all duration-200 placeholder:text-slate-400"
                :placeholder="getFieldLabelPlaceholder(editingField.type)"
          />
        </div>
        
            <!-- Required Field Toggle -->
            <div class="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">Required Field</h4>
                  <p class="text-xs text-slate-500 dark:text-slate-400">Users must fill this field</p>
                </div>
                <div class="relative">
                  <input 
                    type="checkbox" 
                    id="fieldRequired"
                    v-model="editingField.required"
                    class="sr-only"
                  />
                  <label 
                    for="fieldRequired" 
                    class="flex items-center cursor-pointer"
                  >
                    <div class="relative">
                      <div class="w-10 h-5 rounded-full shadow-inner transition-colors duration-200"
                           :style="{ backgroundColor: editingField.required ? '#EF4444' : '#E2E8F0' }"></div>
                      <div class="absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-200 shadow"
                           :class="editingField.required ? 'transform translate-x-5' : ''"></div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <!-- Type-specific configurations -->
            
            <!-- Text Field Configuration -->
            <div v-if="editingField.type === 'text'" class="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
              <div class="flex items-center gap-3 mb-3">
                <div class="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <svg class="w-3 h-3 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16m-7 6h7"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-sm font-semibold text-slate-900 dark:text-white">Text Input Settings</h4>
                  <p class="text-xs text-slate-600 dark:text-slate-400">Configure text input behavior</p>
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Placeholder Text</label>
                  <input 
                    type="text" 
                    v-model="editingField.placeholder"
                    class="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 dark:text-white transition-all text-sm"
                    placeholder="e.g., Enter your full name"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Max Length</label>
                  <input 
                    type="number" 
                    v-model="editingField.maxLength"
                    class="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 dark:text-white transition-all text-sm"
                    placeholder="100"
                    min="1"
                  />
                </div>
              </div>
            </div>

            <!-- Number Field Configuration -->
            <div v-if="editingField.type === 'number'" class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
              <div class="flex items-start gap-4 mb-4">
                <div class="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <svg class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-lg font-bold text-slate-900 dark:text-white">Number Input Settings</h4>
                  <p class="text-sm text-slate-600 dark:text-slate-400">Configure numeric input validation and limits</p>
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Minimum Value</label>
                  <input 
                    type="number" 
                    v-model="editingField.min"
                    class="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-purple-500 text-slate-900 dark:text-white transition-all"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Maximum Value</label>
                  <input 
                    type="number" 
                    v-model="editingField.max"
                    class="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-purple-500 text-slate-900 dark:text-white transition-all"
                    placeholder="999"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Step</label>
                  <input 
                    type="number" 
                    v-model="editingField.step"
                    class="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-purple-500 text-slate-900 dark:text-white transition-all"
                    placeholder="1"
                    min="0.01"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            <!-- Select/Multiselect Options -->
            <div v-if="editingField.type === 'select' || editingField.type === 'multiselect'" class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
              <div class="flex items-start gap-4 mb-6">
                <div class="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <svg class="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-lg font-bold text-slate-900 dark:text-white">
                    {{ editingField.type === 'select' ? 'Dropdown' : 'Multi-Select' }} Options
                  </h4>
                  <p class="text-sm text-slate-600 dark:text-slate-400">
                    {{ editingField.type === 'select' ? 'Users can select one option' : 'Users can select multiple options' }}
                  </p>
                </div>
              </div>
              
              <div class="space-y-3 mb-4">
                <div 
                  v-for="(option, index) in editingField.options" 
                  :key="index"
                  class="flex items-center gap-3 group"
                >
                  <div class="flex-1">
                    <input 
                      type="text" 
                      v-model="editingField.options![index]"
                      class="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-slate-900 dark:text-white transition-all placeholder:text-slate-400"
                      :placeholder="`Option ${index + 1}`"
                    />
                  </div>
            <button 
                    type="button"
                    @click="removeFieldOption(index)"
                    class="p-2.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                    :disabled="editingField.options?.length === 1"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <button 
                type="button"
                @click="addFieldOption"
                class="w-full px-4 py-4 bg-slate-50 dark:bg-slate-900 hover:bg-orange-50 dark:hover:bg-orange-900/20 text-orange-600 dark:text-orange-400 border-2 border-dashed border-orange-200 dark:border-orange-800 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 font-medium hover:border-orange-300 dark:hover:border-orange-700"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
                Add Option
            </button>
          </div>
          
                         <!-- Checkbox Configuration -->
             <div v-if="editingField.type === 'checkbox'" class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
               <div class="flex items-start gap-4 mb-4">
                 <div class="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                   <svg class="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
                 <div>
                   <h4 class="text-lg font-bold text-slate-900 dark:text-white">Checkbox Settings</h4>
                   <p class="text-sm text-slate-600 dark:text-slate-400">Configure yes/no or true/false selection</p>
            </div>
               </div>
                <div>
                 <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Checkbox Label Text</label>
                  <input 
                    type="text" 
                   v-model="editingField.checkboxLabel"
                   class="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-teal-500 text-slate-900 dark:text-white transition-all"
                   placeholder="e.g., I agree to the terms and conditions"
                 />
               </div>
                </div>
                
             <!-- Email Field Configuration -->
             <div v-if="editingField.type === 'email'" class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
               <div class="flex items-start gap-4 mb-4">
                 <div class="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                   <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                   </svg>
                 </div>
                <div>
                   <h4 class="text-lg font-bold text-slate-900 dark:text-white">Email Input Settings</h4>
                   <p class="text-sm text-slate-600 dark:text-slate-400">Email validation and formatting options</p>
                 </div>
               </div>
               <div class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                 <div class="flex items-center gap-3">
                   <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                   </svg>
                   <div class="text-sm text-blue-900 dark:text-blue-100">
                     <p class="font-semibold">Automatic Email Validation</p>
                     <p>This field will automatically validate email format and show error messages for invalid entries.</p>
                   </div>
                 </div>
                </div>
              </div>
              
             <!-- Date Field Configuration -->
             <div v-if="editingField.type === 'date'" class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
               <div class="flex items-start gap-4 mb-4">
                 <div class="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                   <svg class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                   </svg>
                 </div>
                 <div>
                   <h4 class="text-lg font-bold text-slate-900 dark:text-white">Date Picker Settings</h4>
                   <p class="text-sm text-slate-600 dark:text-slate-400">Configure date selection and limits</p>
                 </div>
               </div>
               <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                   <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Minimum Date</label>
                  <input 
                     type="date" 
                     v-model="editingField.minDate"
                     class="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-white transition-all"
                   />
                 </div>
                 <div>
                   <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Maximum Date</label>
                   <input 
                     type="date" 
                     v-model="editingField.maxDate"
                     class="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-white transition-all"
                   />
                 </div>
               </div>
             </div>

             <!-- File Upload Configuration -->
             <div v-if="editingField.type === 'file'" class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
               <div class="flex items-start gap-4 mb-4">
                 <div class="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                   <svg class="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 002.828-2.828l-6.586-6.586a2 2 0 00-2.828 2.828z"></path>
                   </svg>
                 </div>
                 <div>
                   <h4 class="text-lg font-bold text-slate-900 dark:text-white">File Upload Settings</h4>
                   <p class="text-sm text-slate-600 dark:text-slate-400">Configure file types and size limits</p>
                 </div>
               </div>
               <div class="space-y-4">
                 <div>
                   <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Allowed File Types</label>
                   <input 
                     type="text" 
                     v-model="editingField.acceptedTypes"
                     class="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-amber-500 text-slate-900 dark:text-white transition-all"
                     placeholder="e.g., .pdf,.doc,.docx,.jpg,.png"
                   />
                   <p class="text-xs text-slate-500 mt-1">Enter file extensions separated by commas</p>
                 </div>
                 <div>
                   <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Max File Size (MB)</label>
                   <input 
                     type="number" 
                     v-model="editingField.maxFileSize"
                     class="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-amber-500 text-slate-900 dark:text-white transition-all"
                     placeholder="5"
                     min="0.1"
                     step="0.1"
                   />
                 </div>
               </div>
             </div>

             <!-- Simple Fields (no extra configuration needed) -->
             <div v-if="['textarea', 'phone', 'url', 'datetime', 'color', 'user'].includes(editingField.type)" class="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
               <div class="flex items-start gap-4">
                 <div class="w-10 h-10 rounded-xl flex items-center justify-center"
                      :class="{
                        'bg-green-100 dark:bg-green-900/30': editingField.type === 'textarea',
                        'bg-emerald-100 dark:bg-emerald-900/30': editingField.type === 'phone',
                        'bg-sky-100 dark:bg-sky-900/30': editingField.type === 'url',
                        'bg-cyan-100 dark:bg-cyan-900/30': editingField.type === 'datetime',
                        'bg-pink-100 dark:bg-pink-900/30': editingField.type === 'color',
                        'bg-violet-100 dark:bg-violet-900/30': editingField.type === 'user'
                      }">
                   <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        :class="{
                          'text-green-600 dark:text-green-400': editingField.type === 'textarea',
                          'text-emerald-600 dark:text-emerald-400': editingField.type === 'phone',
                          'text-sky-600 dark:text-sky-400': editingField.type === 'url',
                          'text-cyan-600 dark:text-cyan-400': editingField.type === 'datetime',
                          'text-pink-600 dark:text-pink-400': editingField.type === 'color',
                          'text-violet-600 dark:text-violet-400': editingField.type === 'user'
                        }">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getFieldIcon(editingField.type)"></path>
                   </svg>
                 </div>
                 <div>
                   <h4 class="text-lg font-bold text-slate-900 dark:text-white mb-2">{{ getFieldTypeLabel(editingField.type) }} Field</h4>
                   <div class="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                     <p class="text-sm text-slate-600 dark:text-slate-400 mb-2">
                       <span v-if="editingField.type === 'textarea'">Multi-line text area for longer content like descriptions, notes, or detailed information.</span>
                       <span v-else-if="editingField.type === 'phone'">Phone number input with automatic formatting and validation.</span>
                       <span v-else-if="editingField.type === 'url'">Website URL input with link validation and formatting.</span>
                       <span v-else-if="editingField.type === 'datetime'">Date and time picker for scheduling and timestamps.</span>
                       <span v-else-if="editingField.type === 'color'">Color picker for selecting colors with hex code output.</span>
                       <span v-else-if="editingField.type === 'user'">Team member selection from your organization's user list.</span>
                     </p>
                     <div class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                       <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                       </svg>
                       No additional configuration required - ready to use!
                     </div>
                   </div>
                 </div>
               </div>
             </div>

            

          </form>
        </div>

        <!-- Footer -->
        <div class="px-4 py-3 border-t border-slate-200/50 dark:border-slate-700/50 bg-white dark:bg-slate-800">
          <div class="flex items-center justify-end gap-3">
                <button 
              @click="showFieldDetailsModal = false"
              class="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 text-sm font-medium"
            >
              Cancel
            </button>
            <button 
              @click="saveFieldDetails"
              class="px-6 py-2 text-white rounded-lg transition-all duration-200 font-semibold shadow-md hover:shadow-lg flex items-center gap-2 text-sm hover:scale-105"
              style="background: linear-gradient(135deg, #3B82F6, #6366F1);"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
              Create Field
                </button>
              </div>
            </div>
          </div>
        </div>
        
    <!-- Card Creation Modal -->
    <div v-if="showAreaModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black/30 backdrop-blur-md" @click="showAreaModal = false"></div>
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 z-10 relative border border-gray-200 max-h-[85vh] overflow-hidden">
        
        <!-- Header with gradient -->
        <div class="px-6 py-4" style="background: linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%);">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-white/30 rounded-lg flex items-center justify-center border border-white/20">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
          <div>
                <h3 class="text-lg font-semibold text-white">{{ directT('settings.leadsSettings.editCard.title') }}</h3>
                <p class="text-white/90 text-sm">{{ directT('settings.leadsSettings.editCard.subtitle') }}</p>
          </div>
            </div>
            <button @click="showAreaModal = false" class="w-8 h-8 bg-white/30 hover:bg-white/40 rounded-lg flex items-center justify-center transition-all duration-200 border border-white/20">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
          </div>
        </div>
        
        <!-- Content -->
        <div class="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <!-- Card Name -->
          <div>
            <label class="block text-sm font-semibold text-gray-900 mb-2">{{ directT('settings.leadsSettings.cardCreation.cardName') }}</label>
            <input 
              v-model="editingArea.title" 
              type="text"
              :placeholder="directT('settings.leadsSettings.cardCreation.cardNamePlaceholder')"
              class="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-100 focus:border-green-500 bg-gray-50 text-gray-900 transition-all duration-300 placeholder:text-gray-400"
            />
          </div>
          
          <!-- Preview Card -->
          <div class="mt-6 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
            <div class="flex items-center gap-2 mb-2">
              <div class="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
                    <div>
                <h5 class="text-sm font-medium text-gray-900">{{ editingArea.title || directT('settings.leadsSettings.cardCreation.cardNamePreview') }}</h5>
                <p class="text-xs text-gray-500">{{ directT('settings.leadsSettings.cardCreation.customCard') }}</p>
                    </div>
                  </div>
            <p class="text-xs text-gray-500">{{ directT('settings.leadsSettings.cardCreation.previewDescription') }}</p>
             </div>
           </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div class="text-xs text-gray-500">
            {{ directT('settings.leadsSettings.editCard.configureSettings') }}
          </div>
          <div class="flex gap-2">
          <button 
              @click="showAreaModal = false"
              class="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 text-sm font-medium"
            >
              {{ directT('settings.leadsSettings.cardCreation.cancel') }}
            </button>
            <button 
              @click="saveArea"
              :disabled="!editingArea.title.trim()"
              class="px-6 py-2 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium shadow-md disabled:shadow-none"
              style="background: linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%);"
            >
              {{ directT('settings.leadsSettings.editCard.createCard') }}
          </button>
        </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div 
      v-if="toastMessage" 
      class="fixed bottom-4 right-4 left-4 md:left-auto md:max-w-md p-4 rounded-md shadow-lg transform transition-transform duration-300 z-50 flex items-center justify-between"
      :class="{ 
        'bg-green-600 text-white': toastType === 'success', 
        'bg-red-600 text-white': toastType === 'error', 
        'bg-blue-600 text-white': toastType === 'info' 
      }"
    >
      <span>{{ toastMessage }}</span>
      <button @click="closeToast" class="ml-4 text-white hover:text-gray-300">×</button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import SettingsNav from "../SettingsNav.vue";

import { createToast } from "@/utils/toast";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { useI18n } from "vue-i18n";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

// Store direct references to translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

interface StatusLeadRecord {
  id: string;
  status: string;
  color: string;
  organization_id: string;
  created_at?: string;
  updated_at?: string;
}

interface Status {
  id?: string;
  name: string;
  color: string;
  organization_id?: string;
}

interface LeadSourceRecord {
  id: string;
  source: string;
  organization_id: string;
  created_at?: string;
  updated_at?: string;
}

interface Source {
  id?: string;
  name: string;
  organization_id?: string;
}

interface SystemField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'email' | 'select' | 'date' | 'datetime' | 'phone' | 'checkbox' | 'multiselect' | 'url' | 'file' | 'color' | 'user';
  required: boolean;
  options?: string[];
  placeholder?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  step?: number;
  checkboxLabel?: string;
  minDate?: string;
  maxDate?: string;
  acceptedTypes?: string;
  maxFileSize?: number;
}

interface SystemArea {
  id: string;
  title: string;
  fields: SystemField[];
}

export default defineComponent({
  name: "settings.leadsSettings",
  components: {
    SettingsNav,
  },
  setup() {
    const statusList = ref<Status[]>([]);
    const sourceList = ref<Source[]>([]);
    const systemAreas = ref<SystemArea[]>([]);
    const saving = ref(false);
    const organizationId = ref<string | null>(null);
    
    // Default cards that exist in the lead detail view
    const defaultCards = ref([
      {
        id: 'contact-info',
        title: 'פרטי התקשרות',
        iconBg: 'bg-blue-100 dark:bg-blue-900/30',
        iconColor: 'text-blue-600 dark:text-blue-400',
        iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
      },
      {
        id: 'lead-source',
        title: 'מקור הליד',
        iconBg: 'bg-green-100 dark:bg-green-900/30',
        iconColor: 'text-green-600 dark:text-green-400',
        iconPath: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
      },
      {
        id: 'additional-details',
        title: 'פרטי לקוח נוספים',
        iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
        iconColor: 'text-yellow-600 dark:text-yellow-400',
        iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
      },
      {
        id: 'interests',
        title: 'תחומי עניין וצורך',
        iconBg: 'bg-red-100 dark:bg-red-900/30',
        iconColor: 'text-red-600 dark:text-red-400',
        iconPath: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'
      },
      {
        id: 'status-conversation',
        title: 'סטטוס ושיחה',
        iconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
        iconColor: 'text-indigo-600 dark:text-indigo-400',
        iconPath: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.456L3 21l2.456-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z'
      },
      {
        id: 'tags-sorting',
        title: 'תגיות ומיון',
        iconBg: 'bg-pink-100 dark:bg-pink-900/30',
        iconColor: 'text-pink-600 dark:text-pink-400',
        iconPath: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z'
      }
    ]);
    
    const { locale } = useI18n();
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params: Record<string, any> = {}): string => {
      // Normalize keys that start with 'settings.leadsSettings'
      if (key.startsWith('settings.leadsSettings')) {
        key = key.replace('settings.leadsSettings', 'settings.leadsSettings');
      }
      
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      
      if (typeof result !== 'string') {
        console.warn(`Translation key ${key} does not resolve to a string`);
        return key;
      }
      
      // Handle parameter substitution if needed
      if (Object.keys(params).length > 0) {
        let finalResult: string = result;
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          finalResult = finalResult.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
        });
        return finalResult;
      }
      
      return result;
    };
    
    // Status modal
    const showStatusModal = ref(false);
    const editingStatus = ref<Status>({ name: '', color: '#605DFF' });
    const editingStatusIndex = ref(-1);
    
    // Source modal
    const showSourceModal = ref(false);
    const editingSource = ref<Source>({ name: '' });
    const editingSourceIndex = ref(-1);
    
    // Delete confirmation modal
    const showDeleteModal = ref(false);
    const deleteType = ref<'status' | 'source'>('status');
    const deleteIndex = ref<number>(-1);
    
    // Toast notification
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info'>('info');
    const toastTimeout = ref<number | null>(null);
    
    // Drag and Drop state
    const draggedIndex = ref<number>(-1);
    const dragOverIndex = ref<number>(-1);
    
    // Fetch settings on component mount
    onMounted(async () => {
      try {
        // Force reload language if needed
        if (localStorage.getItem('language') === 'he') {
          console.log('Detected Hebrew language, making sure translations are loaded');
          // Force a small delay to make sure everything is loaded
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        organizationId.value = await getCurrentOrgId();
        
        if (!organizationId.value) {
          createToast('User is not associated with an organization', 'error');
          return;
        }
        
        await fetchLeadSettings();
        await loadSystemFields();
      } catch (error) {
        console.error('Error loading lead settings:', error);
        createToast('Failed to load lead settings', 'error');
      }
    });
    
    // Fetch lead settings from the database
    const fetchLeadSettings = async () => {
      try {
        console.log('Fetching lead settings for organization:', organizationId.value);
        
        // Fetch lead statuses from statusleads table
        const { data: statusData, error: statusError } = await supabase
          .from('statusleads')
          .select('*')
          .eq('organization_id', organizationId.value)
          .order('created_at', { ascending: true });
        
        console.log('Status query result:', { statusData, statusError });
        
        if (statusError) throw statusError;
        
        // Map the database records to the Status interface with proper type assertion
        statusList.value = (statusData || []).map((item: any) => ({
          id: item.id as string,
          name: item.status as string,
          color: (item.color as string) || '#605DFF', // Default color if not provided
          organization_id: item.organization_id as string
        }));
        
        console.log('Mapped statusList:', statusList.value);
        
        // If no statuses exist, create default ones
        if (statusList.value.length === 0) {
          console.log('No statuses found, creating defaults...');
          await createDefaultStatuses();
        }
        
        // Fetch lead sources from leadsources table
        const { data: sourceData, error: sourceError } = await supabase
          .from('leadsources')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (sourceError) throw sourceError;
        
        // Map the database records to the Source interface
        sourceList.value = (sourceData || []).map((item: any) => ({
          id: item.id as string,
          name: item.source as string,
          organization_id: item.organization_id as string
        }));
        
        // If no sources exist, create default ones
        if (sourceList.value.length === 0) {
          await createDefaultSources();
        }
      } catch (error) {
        console.error('Error fetching lead settings:', error);
        createToast('Failed to fetch lead settings', 'error');
      }
    };
    
    // Create default statuses if none exist
    const createDefaultStatuses = async () => {
      try {
        const defaultStatuses = [
          { name: 'New', color: '#605DFF' },
          { name: 'In Progress', color: '#10B981' },
          { name: 'Pending', color: '#F59E0B' },
          { name: 'Rejected', color: '#EF4444' },
          { name: 'Close', color: '#10B981' }
        ];
        
        // Insert default statuses into database
        const { data, error } = await supabase
          .from('statusleads')
          .insert(
            defaultStatuses.map((status) => ({
              status: status.name,
              color: status.color,
              organization_id: organizationId.value
            }))
          )
          .select();
        
        if (error) throw error;
        
        // Add default statuses to local state with proper type assertion
        if (data) {
          statusList.value = (data || []).map((item: any) => ({
            id: item.id as string,
            name: item.status as string,
            color: item.color as string,
            organization_id: item.organization_id as string
          }));
        }
      } catch (error) {
        console.error('Error creating default statuses:', error);
        createToast('Failed to create default statuses', 'error');
      }
    };
    
    // Create default sources if none exist
    const createDefaultSources = async () => {
      try {
        const defaultSources = [
          { name: 'Website' },
          { name: 'Referral' },
          { name: 'Social Media' },
          { name: 'Direct' },
          { name: 'Email Campaign' }
        ];
        
        // Insert default sources into database
        const { data, error } = await supabase
          .from('leadsources')
          .insert(
            defaultSources.map(source => ({
              source: source.name,
              organization_id: organizationId.value
            }))
          )
          .select();
        
        if (error) throw error;
        
        // Add default sources to local state
        if (data) {
          sourceList.value = (data || []).map((item: any) => ({
            id: item.id as string,
            name: item.source as string,
            organization_id: item.organization_id as string
          }));
        }
      } catch (error) {
        console.error('Error creating default sources:', error);
        createToast('Failed to create default sources', 'error');
      }
    };
    
    // Status functions
    const addNewStatus = () => {
      editingStatus.value = { name: '', color: '#605DFF' };
      editingStatusIndex.value = -1;
      showStatusModal.value = true;
    };
    
    const editStatus = (index: number) => {
      editingStatus.value = { ...statusList.value[index] };
      editingStatusIndex.value = index;
      showStatusModal.value = true;
    };
    
    const deleteStatus = async (index: number) => {
      deleteType.value = 'status';
      deleteIndex.value = index;
      showDeleteModal.value = true;
    };
    
    const closeStatusModal = () => {
      showStatusModal.value = false;
    };
    
    const saveStatus = async () => {
      try {
        if (editingStatusIndex.value === -1) {
          // Add new status to database
          const { data, error } = await supabase
            .from('statusleads')
            .insert({
              status: editingStatus.value.name,
              color: editingStatus.value.color,
              organization_id: organizationId.value
            })
            .select();
          
          if (error) throw error;
          
          // Add to local list with the database ID and proper type assertion
          if (data && data[0]) {
            const newStatus = data[0] as any;
            statusList.value.push({ 
              id: newStatus.id as string,
              name: newStatus.status as string,
              color: newStatus.color as string,
              organization_id: newStatus.organization_id as string
            });
          }
        } else {
          // Update existing status
          const statusToUpdate = statusList.value[editingStatusIndex.value];
          
          if (statusToUpdate.id) {
            // Update in database
            const { error } = await supabase
              .from('statusleads')
              .update({
                status: editingStatus.value.name,
                color: editingStatus.value.color
              })
              .eq('id', statusToUpdate.id);
            
            if (error) throw error;
          } else {
            // Status doesn't exist in database yet, create it
            const { data, error } = await supabase
              .from('statusleads')
              .insert({
                status: editingStatus.value.name,
                color: editingStatus.value.color,
                organization_id: organizationId.value
              })
              .select();
            
            if (error) throw error;
            
            if (data && data[0]) {
              const newStatus = data[0] as any;
              editingStatus.value.id = newStatus.id as string;
              editingStatus.value.organization_id = newStatus.organization_id as string;
            }
          }
          
          // Update in local list
          statusList.value[editingStatusIndex.value] = { ...editingStatus.value };
        }
        
        createToast('Status saved successfully', 'success');
        closeStatusModal();
      } catch (error) {
        console.error('Error saving status:', error);
        createToast('Failed to save status', 'error');
      }
    };
    
    const saveStatusSettings = async () => {
      saving.value = true;
      
      try {
        // Verify all statuses are saved in database
        console.log('Saving status settings...', statusList.value);
        
        // Save all statuses - both new and modified
        let totalUpdated = 0;
        
        for (const status of statusList.value) {
          if (!status.id) {
            // New status - insert it
            console.log('Inserting new status:', status.name);
            const { data, error } = await supabase
              .from('statusleads')
              .insert({
                status: status.name,
                color: status.color,
                organization_id: organizationId.value
              })
              .select();
            
            if (error) throw error;
            
            if (data && data[0]) {
              // Update the status with the new ID
              const newStatus = data[0] as any;
              status.id = newStatus.id as string;
              status.organization_id = newStatus.organization_id as string;
              totalUpdated++;
            }
          } else {
            // Existing status - check if it needs updating
            console.log('Updating existing status:', status.name);
            const { error } = await supabase
              .from('statusleads')
              .update({
                status: status.name,
                color: status.color
              })
              .eq('id', status.id);
            
            if (error) throw error;
            totalUpdated++;
          }
        }
        
        console.log(`Total statuses processed: ${totalUpdated}`);
        
        // Refresh the data to ensure everything is up to date
        await fetchLeadSettings();
        
        createToast('הגדרות הסטטוסים נשמרו בהצלחה! הסטטוסים החדשים יופיעו עכשיו בדף פרטי הליד ובכל הדפים האחרים.', 'success');
        console.log('Status settings saved successfully. Updated statusList:', statusList.value);
      } catch (error) {
        console.error('Error saving status settings:', error);
        createToast('שגיאה בשמירת הגדרות הסטטוסים', 'error');
      } finally {
        saving.value = false;
      }
    };
    
    // Source functions
    const addNewSource = () => {
      editingSource.value = { name: '' };
      editingSourceIndex.value = -1;
      showSourceModal.value = true;
    };
    
    const editSource = (index: number) => {
      editingSource.value = { ...sourceList.value[index] };
      editingSourceIndex.value = index;
      showSourceModal.value = true;
    };
    
    const deleteSource = async (index: number) => {
      deleteType.value = 'source';
      deleteIndex.value = index;
      showDeleteModal.value = true;
    };
    
    const closeSourceModal = () => {
      showSourceModal.value = false;
    };
    
    const saveSource = async () => {
      try {
        if (editingSourceIndex.value === -1) {
          // Add new source to database
          const { data, error } = await supabase
            .from('leadsources')
            .insert({
              source: editingSource.value.name,
              organization_id: organizationId.value
            })
            .select();
          
          if (error) throw error;
          
          // Add to local list with the database ID
          if (data && data[0]) {
            const newSource = data[0] as any;
            sourceList.value.push({ 
              id: newSource.id as string,
              name: newSource.source as string,
              organization_id: newSource.organization_id as string
            });
          }
        } else {
          // Update existing source
          const sourceToUpdate = sourceList.value[editingSourceIndex.value];
          
          if (sourceToUpdate.id) {
            // Update in database
            const { error } = await supabase
              .from('leadsources')
              .update({
                source: editingSource.value.name
              })
              .eq('id', sourceToUpdate.id);
            
            if (error) throw error;
          } else {
            // Source doesn't exist in database yet, create it
            const { data, error } = await supabase
              .from('leadsources')
              .insert({
                source: editingSource.value.name,
                organization_id: organizationId.value
              })
              .select();
            
            if (error) throw error;
            
            if (data && data[0]) {
              const newSource = data[0] as any;
              editingSource.value.id = newSource.id as string;
              editingSource.value.organization_id = newSource.organization_id as string;
            }
          }
          
          // Update in local list
          sourceList.value[editingSourceIndex.value] = { ...editingSource.value };
        }
        
        createToast('Source saved successfully', 'success');
        closeSourceModal();
      } catch (error) {
        console.error('Error saving source:', error);
        createToast('Failed to save source', 'error');
      }
    };
    
    const saveSourceSettings = async () => {
      saving.value = true;
      
      try {
        // No additional saving needed as each source is saved individually
        createToast('Source settings saved successfully', 'success');
      } catch (error) {
        console.error('Error saving source settings:', error);
        createToast('Failed to save source settings', 'error');
      } finally {
        saving.value = false;
      }
    };
    
    // Delete confirmation modal functions
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      deleteIndex.value = -1;
    };
    
    const confirmDelete = async () => {
      if (deleteType.value === 'status') {
        await performDeleteStatus(deleteIndex.value);
      } else {
        await performDeleteSource(deleteIndex.value);
      }
      closeDeleteModal();
    };
    
    const performDeleteStatus = async (index: number) => {
      const statusToDelete = statusList.value[index];
      
      // If the status has an ID, it exists in the database and needs to be deleted
      if (statusToDelete.id) {
        try {
          const { error } = await supabase
            .from('statusleads')
            .delete()
            .eq('id', statusToDelete.id);
          
          if (error) throw error;
          
          // Remove from local list
          statusList.value.splice(index, 1);
          createToast('Status deleted successfully', 'success');
        } catch (error) {
          console.error('Error deleting status:', error);
          createToast('Failed to delete status', 'error');
        }
      } else {
        // Status was only local, just remove from list
        statusList.value.splice(index, 1);
        createToast('Status deleted successfully', 'success');
      }
    };
    
    const performDeleteSource = async (index: number) => {
      const sourceToDelete = sourceList.value[index];
      
      // If the source has an ID, it exists in the database and needs to be deleted
      if (sourceToDelete.id) {
        try {
          const { error } = await supabase
            .from('leadsources')
            .delete()
            .eq('id', sourceToDelete.id);
          
          if (error) throw error;
          
          // Remove from local list
          sourceList.value.splice(index, 1);
          createToast('Source deleted successfully', 'success');
        } catch (error) {
          console.error('Error deleting source:', error);
          createToast('Failed to delete source', 'error');
        }
      } else {
        // Source was only local, just remove from list
        sourceList.value.splice(index, 1);
        createToast('Source deleted successfully', 'success');
      }
    };
    
    // Toast functions
    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      toastMessage.value = message;
      toastType.value = type;
      
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
      }
      
      toastTimeout.value = window.setTimeout(() => {
        closeToast();
      }, 3000);
    };
    
    const closeToast = () => {
      toastMessage.value = '';
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
        toastTimeout.value = null;
      }
    };
    
    // Drag and Drop functions
    const onDragStart = (event: DragEvent, index: number) => {
      draggedIndex.value = index;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/html', index.toString());
      }
    };
    
    const onDragOver = (event: DragEvent) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    };
    
    const onDragEnter = (event: DragEvent, index: number) => {
      event.preventDefault();
      if (draggedIndex.value !== index) {
        dragOverIndex.value = index;
      }
    };
    
    const onDragLeave = (event: DragEvent) => {
      // Check if we're leaving the container entirely
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;
      
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        dragOverIndex.value = -1;
      }
    };
    
    const onDrop = async (event: DragEvent, dropIndex: number) => {
      event.preventDefault();
      
      const dragIndex = draggedIndex.value;
      if (dragIndex === -1 || dragIndex === dropIndex) {
        return;
      }
      
      // Create a new array with reordered items
      const newStatusList = [...statusList.value];
      const draggedItem = newStatusList[dragIndex];
      
      // Remove the dragged item
      newStatusList.splice(dragIndex, 1);
      
      // Insert it at the new position
      newStatusList.splice(dropIndex, 0, draggedItem);
      
      // Update the local state
      statusList.value = newStatusList;
      
      // Save the new order to the database
      await saveStatusOrder();
      
      // Reset drag state
      draggedIndex.value = -1;
      dragOverIndex.value = -1;
    };
    
    const onDragEnd = () => {
      draggedIndex.value = -1;
      dragOverIndex.value = -1;
    };
    
    // Save the new order of statuses to database (temporarily disabled until sort_order column is added)
    const saveStatusOrder = async () => {
      try {
        // For now, just show success message - order will be maintained in memory
        createToast('סדר הסטטוסים עודכן זמנית', 'success');
        console.log('New status order:', statusList.value.map(s => s.name));
      } catch (error) {
        console.error('Error saving status order:', error);
        createToast('שגיאה בשמירת סדר הסטטוסים', 'error');
        // Reload the data to restore the original order
        await fetchLeadSettings();
      }
    };

    // System Fields modal states
    const showAreaModal = ref(false);
    const editingArea = ref<SystemArea>({ id: '', title: '', fields: [] });
    const editingAreaIndex = ref(-1);
    
    // Field Type Selection Modal
    const showFieldTypeModal = ref(false);
    const selectedFieldType = ref('');
    
    // Card Creation Modal states
    const isAddingCard = ref(false);
    const currentStep = ref(1);

    // System Fields functions
    const addNewArea = () => {
      editingArea.value = { 
        id: Date.now().toString(), 
        title: '', 
        fields: [] 
      };
      editingAreaIndex.value = -1;
      showAreaModal.value = true;
    };
    
    // Alias for addNewArea - for backward compatibility with template
    const addNewCard = () => {
      editingArea.value = { 
        id: Date.now().toString(), 
        title: '', 
        fields: [] 
      };
      editingAreaIndex.value = -1;
      isAddingCard.value = true;
      currentStep.value = 1;
    };

    const editArea = (index: number) => {
      editingArea.value = { ...systemAreas.value[index] };
      editingAreaIndex.value = index;
      showAreaModal.value = true;
    };

    const deleteArea = async (index: number) => {
      const area = systemAreas.value[index];
      
      try {
        if (!organizationId.value) {
          createToast('שגיאה: לא נמצא מזהה ארגון', 'error');
          return;
        }

        // Find and delete the area from database
        const { data: existingData, error: queryError } = await supabase
          .from('customleadsfield')
          .select('id, content')
          .eq('organizationid', organizationId.value!);

        if (queryError) throw queryError;
        
        // Find existing area manually since LIKE with JSON doesn't work reliably
        const existingArea = existingData?.find(item => {
          if (item.content && Array.isArray(item.content)) {
            const content = item.content as any[];
            return content.length > 0 && content[0].areaId === area.id;
          }
          return false;
        });

        if (existingArea) {
          // Delete from database
          const { error: deleteError } = await supabase
            .from('customleadsfield')
            .delete()
            .eq('id', existingArea.id);
          
          if (deleteError) throw deleteError;
          console.log('Deleted area from database:', area.title);
        }
        
        // Remove from local array only after successful database deletion
        systemAreas.value.splice(index, 1);
        createToast('אזור נמחק בהצלחה', 'success');
        
      } catch (error) {
        console.error('Error deleting area:', error);
        createToast('שגיאה במחיקת האזור', 'error');
      }
    };

    const saveSystemFields = async () => {
      if (!organizationId.value) {
        createToast('שגיאה: לא נמצא מזהה ארגון', 'error');
        return;
      }

      saving.value = true;
      try {
        // Save each area to the database using the existing customleadsfield table
        for (const area of systemAreas.value) {
          // Convert area to the format expected by customleadsfield
          const fieldsToSave = area.fields.map(field => ({
            key: `${area.id}_${field.id}`,
            label: `${area.title} - ${field.label}`,
            type: field.type,
            required: field.required,
            area: area.title,
            areaId: area.id
          }));

                // Check if this area already exists in database  
      const { data: existingData, error: queryError } = await supabase
        .from('customleadsfield')
        .select('id, content')
        .eq('organizationid', organizationId.value!);

      if (queryError) throw queryError;
      
      // Find existing area manually since LIKE with JSON doesn't work reliably
      const existingArea = existingData?.find(item => {
        if (item.content && Array.isArray(item.content)) {
          const content = item.content as any[];
          return content.length > 0 && content[0].areaId === area.id;
        }
        return false;
      });

                  if (existingArea) {
          // Update existing area
          const { error: updateError } = await supabase
            .from('customleadsfield')
            .update({
              content: fieldsToSave as any,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingArea.id);

          if (updateError) throw updateError;
          console.log('Updated existing area:', area.title);
        } else {
          // Insert new area
          const { error: insertError } = await supabase
            .from('customleadsfield')
            .insert({
              organizationid: organizationId.value!,
              content: fieldsToSave as any,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (insertError) throw insertError;
          console.log('Inserted new area:', area.title);
        }
        }

        createToast('שדות המערכת נשמרו בהצלחה! השדות יופיעו עכשיו בפרטי הליד.', 'success');
        console.log('System fields saved successfully:', systemAreas.value);
        console.log('Saved areas count:', systemAreas.value.length);
      } catch (error: any) {
        console.error('Error saving system fields:', error);
        console.error('Error details:', error.message || error);
        
        // More specific error message
        const errorMessage = error.message || error.toString() || 'שגיאה לא ידועה';
        createToast(`שגיאה בשמירת שדות המערכת: ${errorMessage}`, 'error');
      } finally {
        saving.value = false;
      }
    };

    // Load existing system fields from database
    const loadSystemFields = async () => {
      if (!organizationId.value) return;

      try {
        const { data, error } = await supabase
          .from('customleadsfield')
          .select('id, content')
          .eq('organizationid', organizationId.value!);

        if (error) throw error;

        // Filter and load system areas
        const loadedAreas: SystemArea[] = [];
        
        if (data) {
          for (const record of data) {
            if (record.content && Array.isArray(record.content)) {
              const content = record.content as any[];
              
              // Check if this is a system area (has areaId)
              if (content.length > 0 && content[0].areaId) {
                const areaId = content[0].areaId;
                const areaTitle = content[0].area;
                
                // Check if we already have this area
                let existingArea = loadedAreas.find(area => area.id === areaId);
                
                if (!existingArea) {
                  existingArea = {
                    id: areaId,
                    title: areaTitle,
                    fields: []
                  };
                  loadedAreas.push(existingArea);
                }
                
                // Add fields to the area
                for (const fieldData of content) {
                  const field: SystemField = {
                    id: fieldData.key.split('_')[1] || fieldData.key,
                    label: fieldData.label.replace(`${areaTitle} - `, ''),
                    type: fieldData.type,
                    required: fieldData.required || false
                  };
                  existingArea.fields.push(field);
                }
              }
            }
          }
        }

        systemAreas.value = loadedAreas;
        console.log('System areas loaded:', systemAreas.value);
        console.log('Total areas found:', loadedAreas.length);
      } catch (error) {
        console.error('Error loading system fields:', error);
      }
    };

    // Helper functions for field types
    const getFieldTypeLabel = (type: string) => {
      const typeMap = {
        text: 'text',
        textarea: 'longText',
        number: 'number',
        email: 'email',
        select: 'dropdown',
        date: 'date',
        datetime: 'dateTime',
        phone: 'phone',
        checkbox: 'checkbox',
        multiselect: 'multiSelect',
        url: 'url',
        file: 'fileUpload',
        color: 'colorPicker',
        user: 'userSelect'
      };
      const mappedType = typeMap[type as keyof typeof typeMap] || 'text';
      return directT(`settings.leadsSettings.fieldTypes.${mappedType}`);
    };

    const getFieldTypeIcon = (type: string) => {
      const icons = {
        text: {
          bgClass: 'bg-blue-100 text-blue-600',
          iconClass: 'text-blue-600',
          path: 'M4 6h16M4 12h16m-7 6h7'
        },
        textarea: {
          bgClass: 'bg-green-100 text-green-600',
          iconClass: 'text-green-600',
          path: 'M4 6h16M4 10h16M4 14h16M4 18h7'
        },
        number: {
          bgClass: 'bg-purple-100 text-purple-600',
          iconClass: 'text-purple-600',
          path: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14'
        },
        email: {
          bgClass: 'bg-red-100 text-red-600',
          iconClass: 'text-red-600',
          path: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
        },
        select: {
          bgClass: 'bg-orange-100 text-orange-600',
          iconClass: 'text-orange-600',
          path: 'M7 10l5 5 5-5z'
        },
        date: {
          bgClass: 'bg-indigo-100 text-indigo-600',
          iconClass: 'text-indigo-600',
          path: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
        },
        datetime: {
          bgClass: 'bg-cyan-100 text-cyan-600',
          iconClass: 'text-cyan-600',
          path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
        },
        phone: {
          bgClass: 'bg-emerald-100 text-emerald-600',
          iconClass: 'text-emerald-600',
          path: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
        },
        checkbox: {
          bgClass: 'bg-teal-100 text-teal-600',
          iconClass: 'text-teal-600',
          path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
        },
        multiselect: {
          bgClass: 'bg-lime-100 text-lime-600',
          iconClass: 'text-lime-600',
          path: 'M4 6h16M4 12h16M4 18h16'
        },
        url: {
          bgClass: 'bg-sky-100 text-sky-600',
          iconClass: 'text-sky-600',
          path: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
        },
        file: {
          bgClass: 'bg-amber-100 text-amber-600',
          iconClass: 'text-amber-600',
          path: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 002.828-2.828l-6.586-6.586a2 2 0 00-2.828 2.828z'
        },
        color: {
          bgClass: 'bg-pink-100 text-pink-600',
          iconClass: 'text-pink-600',
          path: 'M7 21a4 4 0 01-4-4c0-5 4-7 4-7s4 2 4 7a4 4 0 01-4 4zM20.2 8l-7.1 7.1a4.5 4.5 0 101.4 1.4L21.6 9.4a2.1 2.1 0 00-1.4-1.4z'
        },
        user: {
          bgClass: 'bg-violet-100 text-violet-600',
          iconClass: 'text-violet-600',
          path: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
        }
      };
      return icons[type as keyof typeof icons] || icons.text;
    };

    // Get field icon path for SVG
    const getFieldIcon = (type: string) => {
      const iconPaths = {
        text: 'M4 6h16M4 12h16m-7 6h7',
        textarea: 'M4 6h16M4 10h16M4 14h16M4 18h7',
        number: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14',
        email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
        phone: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
        url: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
        date: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
        datetime: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        select: 'M19 9l-7 7-7-7',
        multiselect: 'M4 6h16M4 12h16M4 18h16',
        checkbox: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        file: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a2 2 0 002.828-2.828l-6.586-6.586a2 2 0 00-2.828 2.828z',
        color: 'M7 21a4 4 0 01-4-4c0-5 4-7 4-7s4 2 4 7a4 4 0 01-4 4zM20.2 8l-7.1 7.1a4.5 4.5 0 101.4 1.4L21.6 9.4a2.1 2.1 0 00-1.4-1.4z',
        user: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
      };
      return iconPaths[type as keyof typeof iconPaths] || iconPaths.text;
    };

    // Get field type colors with specific color codes
    const getFieldTypeIconColors = (type: string) => {
      const colors = {
        text: {
          background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
          icon: '#2563eb'
        },
        textarea: {
          background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
          icon: '#16a34a'
        },
        number: {
          background: 'linear-gradient(135deg, #f3e8ff, #e9d5ff)',
          icon: '#9333ea'
        },
        email: {
          background: 'linear-gradient(135deg, #fef2f2, #fecaca)',
          icon: '#dc2626'
        },
        phone: {
          background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
          icon: '#059669'
        },
        url: {
          background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
          icon: '#0284c7'
        },
        date: {
          background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
          icon: '#4338ca'
        },
        datetime: {
          background: 'linear-gradient(135deg, #ecfeff, #cffafe)',
          icon: '#0891b2'
        },
        select: {
          background: 'linear-gradient(135deg, #fff7ed, #fed7aa)',
          icon: '#ea580c'
        },
        multiselect: {
          background: 'linear-gradient(135deg, #f7fee7, #ecfccb)',
          icon: '#65a30d'
        },
        checkbox: {
          background: 'linear-gradient(135deg, #f0fdfa, #ccfbf1)',
          icon: '#0d9488'
        },
        file: {
          background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
          icon: '#d97706'
        },
        color: {
          background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)',
          icon: '#ec4899'
        },
        user: {
          background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
          icon: '#7c3aed'
        }
      };
      return colors[type as keyof typeof colors] || colors.text;
    };

    // Get field label placeholder based on field type
    const getFieldLabelPlaceholder = (type: string) => {
      const placeholders = {
        text: 'e.g., Full Name, Company Name, Job Title...',
        textarea: 'e.g., Description, Notes, Comments...',
        number: 'e.g., Age, Budget, Quantity, Score...',
        email: 'e.g., Email Address, Contact Email...',
        phone: 'e.g., Phone Number, Mobile, Contact Number...',
        url: 'e.g., Website, Portfolio URL, LinkedIn...',
        date: 'e.g., Birth Date, Start Date, Deadline...',
        datetime: 'e.g., Meeting Time, Event Date, Timestamp...',
        select: 'e.g., Status, Priority, Category, Region...',
        multiselect: 'e.g., Skills, Interests, Tags, Categories...',
        checkbox: 'e.g., Newsletter Subscription, Terms Agreement...',
        file: 'e.g., Resume, Portfolio, Document Upload...',
        color: 'e.g., Brand Color, Theme Color, Preference...',
        user: 'e.g., Assigned To, Team Member, Contact Person...'
      };
      return placeholders[type as keyof typeof placeholders] || placeholders.text;
    };

    // Field types with icons and descriptions - using computed to get translated names
    const fieldTypes = computed(() => [
      {
        type: 'text',
        name: directT('settings.leadsSettings.fieldTypes.text'),
        description: 'Single line text input'
      },
      {
        type: 'textarea',
        name: directT('settings.leadsSettings.fieldTypes.longText'),
        description: 'Multi-line text area'
      },
      {
        type: 'number',
        name: directT('settings.leadsSettings.fieldTypes.number'),
        description: 'Numeric input field'
      },
      {
        type: 'email',
        name: directT('settings.leadsSettings.fieldTypes.email'),
        description: 'Email address with validation'
      },
      {
        type: 'phone',
        name: directT('settings.leadsSettings.fieldTypes.phone'),
        description: 'Phone number input'
      },
      {
        type: 'url',
        name: directT('settings.leadsSettings.fieldTypes.url'),
        description: 'Website link or URL'
      },
      {
        type: 'date',
        name: directT('settings.leadsSettings.fieldTypes.date'),
        description: 'Date picker input'
      },
      {
        type: 'datetime',
        name: directT('settings.leadsSettings.fieldTypes.dateTime'),
        description: 'Date and time picker'
      },
      {
        type: 'select',
        name: directT('settings.leadsSettings.fieldTypes.dropdown'),
        description: 'Single selection dropdown'
      },
      {
        type: 'multiselect',
        name: directT('settings.leadsSettings.fieldTypes.multiSelect'),
        description: 'Multiple selection options'
      },
      {
        type: 'checkbox',
        name: directT('settings.leadsSettings.fieldTypes.checkbox'),
        description: 'Yes/No or true/false option'
      },
      {
        type: 'file',
        name: directT('settings.leadsSettings.fieldTypes.fileUpload'),
        description: 'File attachment field'
      },
      {
        type: 'color',
        name: directT('settings.leadsSettings.fieldTypes.colorPicker'),
        description: 'Color selection input'
      },
      {
        type: 'user',
        name: directT('settings.leadsSettings.fieldTypes.userSelect'),
        description: 'Team member assignment'
      }
    ]);

    // Field Details Modal
    const showFieldDetailsModal = ref(false);
    const editingField = ref<SystemField>({
      id: '',
      label: '',
      type: 'text',
      required: false,
      options: []
    });

    // Modal field management functions
    const addFieldToArea = () => {
      showFieldTypeModal.value = true;
    };

    const selectFieldType = (fieldType: string) => {
      selectedFieldType.value = fieldType;
      
      // Initialize new field based on type
      editingField.value = {
        id: Date.now().toString(),
        label: '',
        type: fieldType as SystemField['type'],
        required: false,
        options: fieldType === 'select' || fieldType === 'multiselect' ? ['Option 1'] : []
      };
      
      // Stay in the same modal, just change the content
      showFieldTypeModal.value = false;
      showFieldDetailsModal.value = true;
    };

    const saveFieldDetails = () => {
      if (!editingField.value.label.trim()) {
        createToast('Please enter a field label', 'error');
        return;
      }

      // Validate select/multiselect options
      if ((editingField.value.type === 'select' || editingField.value.type === 'multiselect') && 
          (!editingField.value.options || editingField.value.options.length === 0 || 
           editingField.value.options.every(opt => !opt.trim()))) {
        createToast('Please add at least one option for the dropdown', 'error');
        return;
      }

      // Add field to area
      editingArea.value.fields.push({ ...editingField.value });
      
      // Close field details modal and return to field type selection
      showFieldDetailsModal.value = false;
      showFieldTypeModal.value = true;
      
      createToast('Field added successfully', 'success');
    };

    const addFieldOption = () => {
      if (!editingField.value.options) {
        editingField.value.options = [];
      }
      editingField.value.options.push('');
    };

    const removeFieldOption = (index: number) => {
      if (editingField.value.options) {
        editingField.value.options.splice(index, 1);
      }
    };

    const removeFieldFromArea = (fieldIndex: number) => {
      editingArea.value.fields.splice(fieldIndex, 1);
    };

    // Option management for select fields
    const addOption = (fieldIndex: number) => {
      if (!editingArea.value.fields[fieldIndex].options) {
        editingArea.value.fields[fieldIndex].options = [];
      }
      editingArea.value.fields[fieldIndex].options!.push('');
    };

    const removeOption = (fieldIndex: number, optionIndex: number) => {
      if (editingArea.value.fields[fieldIndex].options) {
        editingArea.value.fields[fieldIndex].options!.splice(optionIndex, 1);
      }
    };

    const saveArea = async () => {
      console.log('saveArea called with:', editingArea.value);
      
      if (!editingArea.value.title.trim()) {
        createToast('נא להזין כותרת לאזור', 'error');
        return;
      }

      if (editingArea.value.fields.length === 0) {
        createToast('נא להוסיף לפחות שדה אחד לאזור', 'error');
        return;
      }

      // Validate that all fields have labels
      const invalidFields = editingArea.value.fields.filter(field => !field.label.trim());
      if (invalidFields.length > 0) {
        createToast('נא למלא תווית לכל השדות', 'error');
        return;
      }

      if (editingAreaIndex.value === -1) {
        // Add new area
        systemAreas.value.push({ ...editingArea.value });
        createToast('אזור חדש נוסף בהצלחה', 'success');
      } else {
        // Update existing area
        systemAreas.value[editingAreaIndex.value] = { ...editingArea.value };
        createToast('אזור עודכן בהצלחה', 'success');
      }

      // Save to database immediately
      await saveSystemFields();

      showAreaModal.value = false;
    };

    // Function to proceed to field selection after card name is set
    const proceedToFieldSelection = () => {
      if (!editingArea.value.title.trim()) {
        createToast('Please enter a card name', 'error');
        return;
      }
      
      // Move to step 2
      currentStep.value = 2;
    };
    
    // Function to edit field in card (during creation)
    const editFieldInCard = (fieldIndex: number) => {
      editingField.value = { ...editingArea.value.fields[fieldIndex] };
      showFieldDetailsModal.value = true;
    };

    // Function to remove field from card during creation
    const removeFieldFromCard = (fieldId: string) => {
      const fieldIndex = editingArea.value.fields.findIndex(field => field.id === fieldId);
      if (fieldIndex > -1) {
        editingArea.value.fields.splice(fieldIndex, 1);
        createToast('Field removed', 'success');
      }
    };

    // Function to finish card creation and save
    const finishCardCreation = async () => {
      if (editingArea.value.fields.length === 0) {
        createToast('Please add at least one field to the card', 'error');
        return;
      }

      // Add or update the area
      if (editingAreaIndex.value === -1) {
        // Add new area
        systemAreas.value.push({ ...editingArea.value });
        createToast('Card created successfully', 'success');
      } else {
        // Update existing area
        systemAreas.value[editingAreaIndex.value] = { ...editingArea.value };
        createToast('Card updated successfully', 'success');
      }

      // Save to database
      await saveSystemFields();

      // Close modals
      showFieldTypeModal.value = false;
      showFieldDetailsModal.value = false;
      showAreaModal.value = false;
    };

    // Function to edit default cards (convert them to custom cards)
    const editDefaultCard = (cardIndex: number) => {
      const defaultCard = defaultCards.value[cardIndex];
      
      // Create a new custom area based on the default card
      const newArea: SystemArea = {
        id: `default-${defaultCard.id}-${Date.now()}`,
        title: defaultCard.title,
        fields: [] // Start with empty fields, user will add them
      };

      // Set editing state
      editingArea.value = newArea;
      editingAreaIndex.value = -1; // New area
      
      // Open field type selection modal directly
      showFieldTypeModal.value = true;
    };

    // Function to cancel card creation without saving
    const cancelCardCreation = () => {
      // Simply close all modals without saving
      showFieldTypeModal.value = false;
      showFieldDetailsModal.value = false;
      showAreaModal.value = false;
      isAddingCard.value = false;
      
      // Reset editing state
      editingArea.value = { id: '', title: '', fields: [] };
      editingAreaIndex.value = -1;
      currentStep.value = 1;
    };
    
    return {
      statusList,
      sourceList,
      systemAreas,
      defaultCards,
      saving,
      
      // Status modal
      showStatusModal,
      editingStatus,
      editingStatusIndex,
      addNewStatus,
      editStatus,
      deleteStatus,
      closeStatusModal,
      saveStatus,
      saveStatusSettings,
      
      // Source modal
      showSourceModal,
      editingSource,
      editingSourceIndex,
      addNewSource,
      editSource,
      deleteSource,
      closeSourceModal,
      saveSource,
      saveSourceSettings,
      
      // System Fields
      showAreaModal,
      editingArea,
      editingAreaIndex,
      addNewArea,
      addNewCard,
      editArea,
      deleteArea,
      saveSystemFields,
      getFieldTypeLabel,
      getFieldTypeIcon,
      getFieldTypeIconColors,
      getFieldLabelPlaceholder,
      addFieldToArea,
      removeFieldFromArea,
      addOption,
      removeOption,
      saveArea,
      proceedToFieldSelection,
      removeFieldFromCard,
      finishCardCreation,
      editDefaultCard,
      cancelCardCreation,
      editFieldInCard,
      
      // Card Creation Modal states
      isAddingCard,
      currentStep,
      
      // Field Type Selection
      showFieldTypeModal,
      selectedFieldType,
      fieldTypes,
      selectFieldType,
      getFieldIcon,
      
      // Field Details Modal
      showFieldDetailsModal,
      editingField,
      saveFieldDetails,
      addFieldOption,
      removeFieldOption,
      
      // Delete confirmation modal
      showDeleteModal,
      deleteType,
      closeDeleteModal,
      confirmDelete,
      
      // Toast
      toastMessage,
      toastType,
      closeToast,
      
      // Drag and Drop
      draggedIndex,
      dragOverIndex,
      onDragStart,
      onDragOver,
      onDragEnter,
      onDragLeave,
      onDrop,
      onDragEnd,
      
      // Translation
      directT,
      
      // Debug functions
      fetchLeadSettings,
      loadSystemFields
    };
  },
});
</script>

<style scoped>
/* Custom scrollbar styles */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Dark mode scrollbar */
.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Drag and Drop styles */
.dragging {
  opacity: 0.6;
  transform: rotate(5deg);
  z-index: 1000;
}

.drag-over {
  border-color: #9333EA !important;
  background-color: rgba(147, 51, 234, 0.1) !important;
  transform: translateY(-2px);
}

.drag-placeholder {
  border: 2px dashed #9333EA;
  background: rgba(147, 51, 234, 0.1);
  height: 80px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9333EA;
  font-weight: 600;
}

[draggable="true"] {
  transition: all 0.3s ease;
}

[draggable="true"]:hover {
  cursor: grab;
}

[draggable="true"]:active {
  cursor: grabbing;
}

  /* Clean, minimal styles - Professional Design System */

/* Dark mode support */
.dark label {
  color: #f1f5f9 !important;
}

.dark .form-group label {
  color: #f1f5f9 !important;
}

.dark .color-control label {
  color: #f1f5f9 !important;
}

.dark input[type="text"],
.dark input[type="email"],
.dark input[type="tel"],
.dark input[type="number"],
.dark input[type="date"],
.dark input[type="time"],
.dark input[type="password"],
.dark input[type="file"],
.dark select,
.dark textarea {
  background-color: #475569 !important;
  border-color: #64748b !important;
  color: #f1f5f9 !important;
}

.dark input[type="text"]:focus,
.dark input[type="email"]:focus,
.dark input[type="tel"]:focus,
.dark input[type="number"]:focus,
.dark input[type="date"]:focus,
.dark input[type="time"]:focus,
.dark input[type="password"]:focus,
.dark input[type="file"]:focus,
.dark select:focus,
.dark textarea:focus {
  border-color: #10B981 !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
}

.dark input[type="text"]::placeholder,
.dark input[type="email"]::placeholder,
.dark input[type="tel"]::placeholder,
.dark input[type="number"]::placeholder,
.dark input[type="password"]::placeholder,
.dark textarea::placeholder {
  color: #94a3b8 !important;
}

.dark select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23f1f5f9' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.5rem center !important;
  background-size: 1.5em 1.5em !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  padding-right: 2.5rem !important;
}

.dark input:disabled,
.dark select:disabled,
.dark textarea:disabled {
  background-color: #374151 !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
}

/* General dark mode overrides */
.dark .bg-white {
  background-color: #1e293b !important;
}

.dark .bg-gray-50 {
  background-color: #0f172a !important;
}

.dark .bg-gray-100 {
  background-color: #475569 !important;
}

.dark .border-gray-100 {
  border-color: #374151 !important;
}

.dark .border-gray-200 {
  border-color: #475569 !important;
}

.dark .border-gray-300 {
  border-color: #64748b !important;
}

.dark .text-slate-900 {
  color: #f1f5f9 !important;
}

.dark .text-slate-800 {
  color: #f1f5f9 !important;
}

.dark .text-slate-700 {
  color: #e2e8f0 !important;
}

.dark .text-slate-600 {
  color: #cbd5e1 !important;
}

.dark .text-slate-500 {
  color: #94a3b8 !important;
}

.dark .text-slate-400 {
  color: #64748b !important;
}

.dark .text-slate-300 {
  color: #cbd5e1 !important;
}

.dark .text-gray-900 {
  color: #f1f5f9 !important;
}

.dark .text-gray-800 {
  color: #f1f5f9 !important;
}

.dark .text-gray-700 {
  color: #e2e8f0 !important;
}

.dark .text-gray-600 {
  color: #cbd5e1 !important;
}

.dark .text-gray-500 {
  color: #94a3b8 !important;
}

.dark .text-gray-400 {
  color: #64748b !important;
}

/* Card backgrounds */
.dark .bg-slate-800 {
  background-color: #1e293b !important;
}

.dark .bg-slate-900 {
  background-color: #0f172a !important;
}

.dark .bg-slate-700 {
  background-color: #334155 !important;
}

.dark .bg-slate-600 {
  background-color: #475569 !important;
}

.dark .bg-slate-50 {
  background-color: #475569 !important;
}

.dark .bg-slate-100 {
  background-color: #64748b !important;
}

/* Border colors */
.dark .border-slate-700 {
  border-color: #334155 !important;
}

.dark .border-slate-600 {
  border-color: #475569 !important;
}

.dark .border-slate-200 {
  border-color: #475569 !important;
}

/* Status and source list item backgrounds */
.dark .bg-slate-700\/50 {
  background-color: rgba(51, 65, 85, 0.5) !important;
}

.dark .hover\:bg-slate-100:hover {
  background-color: #64748b !important;
}

.dark .hover\:bg-slate-700:hover {
  background-color: #64748b !important;
}

/* Button backgrounds for edit/delete */
.dark .bg-slate-200 {
  background-color: #64748b !important;
}

.dark .hover\:bg-slate-300:hover {
  background-color: #94a3b8 !important;
}

.dark .bg-slate-600 {
  background-color: #475569 !important;
}

.dark .hover\:bg-slate-500:hover {
  background-color: #64748b !important;
}

/* Delete button colors */
.dark .bg-red-100 {
  background-color: rgba(239, 68, 68, 0.2) !important;
}

.dark .hover\:bg-red-200:hover {
  background-color: rgba(239, 68, 68, 0.3) !important;
}

.dark .bg-red-900\/30 {
  background-color: rgba(127, 29, 29, 0.3) !important;
}

.dark .hover\:bg-red-900\/50:hover {
  background-color: rgba(127, 29, 29, 0.5) !important;
}

.dark .text-red-600 {
  color: #dc2626 !important;
}

.dark .text-red-400 {
  color: #f87171 !important;
}

/* Icon backgrounds for empty states */
.dark .bg-emerald-100 {
  background-color: rgba(16, 185, 129, 0.2) !important;
}

.dark .bg-emerald-900\/30 {
  background-color: rgba(6, 78, 59, 0.3) !important;
}

.dark .text-emerald-600 {
  color: #10b981 !important;
}

.dark .text-emerald-400 {
  color: #34d399 !important;
}

/* Auto-save indicator backgrounds */
.dark .bg-green-50 {
  background-color: rgba(34, 197, 94, 0.1) !important;
}

.dark .text-green-600 {
  color: #16a34a !important;
}

.dark .bg-blue-50 {
  background-color: rgba(59, 130, 246, 0.1) !important;
}

.dark .text-blue-600 {
  color: #2563eb !important;
}

/* Modal backgrounds */
.dark .bg-white\/95 {
  background-color: rgba(30, 41, 59, 0.95) !important;
}

.dark .bg-slate-800\/95 {
  background-color: rgba(15, 23, 42, 0.95) !important;
}

.dark .bg-slate-700\/50 {
  background-color: rgba(51, 65, 85, 0.5) !important;
}

/* Field type cards */
.dark .bg-purple-50 {
  background-color: rgba(168, 85, 247, 0.1) !important;
}

.dark .bg-blue-50 {
  background-color: rgba(59, 130, 246, 0.1) !important;
}

.dark .bg-green-50 {
  background-color: rgba(34, 197, 94, 0.1) !important;
}

.dark .bg-orange-50 {
  background-color: rgba(249, 115, 22, 0.1) !important;
}

.dark .bg-red-50 {
  background-color: rgba(239, 68, 68, 0.1) !important;
}

.dark .bg-yellow-50 {
  background-color: rgba(234, 179, 8, 0.1) !important;
}

.dark .bg-indigo-50 {
  background-color: rgba(99, 102, 241, 0.1) !important;
}

.dark .bg-pink-50 {
  background-color: rgba(236, 72, 153, 0.1) !important;
}

/* Field type text colors */
.dark .text-purple-600 {
  color: #a855f7 !important;
}

.dark .text-blue-600 {
  color: #2563eb !important;
}

.dark .text-green-600 {
  color: #16a34a !important;
}

.dark .text-orange-600 {
  color: #ea580c !important;
}

.dark .text-red-600 {
  color: #dc2626 !important;
}

.dark .text-yellow-600 {
  color: #ca8a04 !important;
}

.dark .text-indigo-600 {
  color: #4f46e5 !important;
}

.dark .text-pink-600 {
  color: #db2777 !important;
}

/* Border colors for field type cards */
.dark .border-purple-200 {
  border-color: rgba(168, 85, 247, 0.3) !important;
}

.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.3) !important;
}

.dark .border-green-200 {
  border-color: rgba(34, 197, 94, 0.3) !important;
}

.dark .border-orange-200 {
  border-color: rgba(249, 115, 22, 0.3) !important;
}

.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.3) !important;
}

.dark .border-yellow-200 {
  border-color: rgba(234, 179, 8, 0.3) !important;
}

.dark .border-indigo-200 {
  border-color: rgba(99, 102, 241, 0.3) !important;
}

.dark .border-pink-200 {
  border-color: rgba(236, 72, 153, 0.3) !important;
}

/* Color picker and other form elements */
.dark input[type="color"] {
  background-color: #475569 !important;
  border-color: #64748b !important;
}

.dark input[type="color"]:focus {
  border-color: #10B981 !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
}

/* Toast message styling */
.dark .bg-emerald-800 {
  color: #065f46 !important;
}

.dark .text-emerald-200 {
  color: #a7f3d0 !important;
}

.dark .text-red-800 {
  color: #991b1b !important;
}

.dark .text-red-200 {
  color: #fecaca !important;
}

.dark .text-blue-800 {
  color: #1e40af !important;
}

.dark .text-blue-200 {
  color: #bfdbfe !important;
}

/* Shadow adjustments for dark mode */
.dark .shadow-xl {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4) !important;
}

/* Hover effects for dark mode */
.dark .hover\:shadow-xl:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
}

.dark .hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

/* Ensure all text elements have proper contrast */
.dark h1,
.dark h2,
.dark h3,
.dark h4,
.dark h5,
.dark h6 {
  color: #f1f5f9 !important;
}

.dark p {
  color: #cbd5e1 !important;
}

.dark span {
  color: inherit !important;
}

.dark ul {
  color: inherit !important;
}

.dark li {
  color: inherit !important;
}

/* Override any remaining white backgrounds */
.dark [class*="bg-white"] {
  background-color: #1e293b !important;
}

.dark [class*="bg-gray-50"] {
  background-color: #0f172a !important;
}

.dark [class*="bg-gray-100"] {
  background-color: #475569 !important;
}

/* Drag and drop dark mode */
.dark .drag-over {
  border-color: #10B981 !important;
  background-color: rgba(16, 185, 129, 0.1) !important;
}

.dark .drag-placeholder {
  border-color: #10B981 !important;
  background: rgba(16, 185, 129, 0.1) !important;
  color: #10B981 !important;
}

/* Keep gradient buttons with their original colors */

/* Custom Cards Section Dark Mode */
.dark .bg-slate-50 {
  background-color: #1e293b !important;
}

.dark .bg-slate-100 {
  background-color: #334155 !important;
}

.dark .border-slate-200 {
  border-color: #475569 !important;
}

.dark .border-slate-300 {
  border-color: #64748b !important;
}

.dark .text-slate-900 {
  color: #f1f5f9 !important;
}

.dark .text-slate-600 {
  color: #cbd5e1 !important;
}

.dark .text-slate-500 {
  color: #94a3b8 !important;
}

.dark .text-slate-400 {
  color: #64748b !important;
}

.dark .hover\:border-slate-300:hover {
  border-color: #64748b !important;
}

.dark .hover\:border-slate-500:hover {
  border-color: #94a3b8 !important;
}

.dark .hover\:text-slate-600:hover {
  color: #cbd5e1 !important;
}

.dark .hover\:bg-slate-200:hover {
  background-color: #475569 !important;
}

.dark .hover\:bg-slate-600:hover {
  background-color: #475569 !important;
}

.dark .hover\:bg-red-100:hover {
  background-color: rgba(239, 68, 68, 0.2) !important;
}

.dark .hover\:bg-red-900\/30:hover {
  background-color: rgba(127, 29, 29, 0.3) !important;
}

/* Field Type Modal Dark Mode */
.dark .bg-slate-800 {
  background-color: #1e293b !important;
}

.dark .border-slate-700 {
  border-color: #475569 !important;
}

.dark .bg-slate-900 {
  background-color: #0f172a !important;
}

.dark .bg-slate-700\/50 {
  background-color: rgba(51, 65, 85, 0.5) !important;
}

.dark .bg-slate-800\/50 {
  background-color: rgba(30, 41, 59, 0.5) !important;
}

/* Field Configuration Modal Dark Mode */
.dark .bg-gray-900 {
  background-color: #0f172a !important;
}

.dark .border-slate-200\/50 {
  border-color: rgba(226, 232, 240, 0.1) !important;
}

.dark .border-slate-700\/50 {
  border-color: rgba(71, 85, 105, 0.5) !important;
}

.dark .bg-slate-50\/30 {
  background-color: rgba(248, 250, 252, 0.05) !important;
}

.dark .bg-slate-900\/30 {
  background-color: rgba(15, 23, 42, 0.3) !important;
}

/* Field Type Colors in Dark Mode */
.dark .text-teal-600 {
  color: #0d9488 !important;
}

.dark .text-teal-400 {
  color: #2dd4bf !important;
}

.dark .bg-teal-100 {
  background-color: rgba(13, 148, 136, 0.2) !important;
}

.dark .bg-teal-900\/30 {
  background-color: rgba(19, 78, 74, 0.3) !important;
}

.dark .text-emerald-600 {
  color: #059669 !important;
}

.dark .text-emerald-400 {
  color: #34d399 !important;
}

.dark .bg-emerald-100 {
  background-color: rgba(5, 150, 105, 0.2) !important;
}

.dark .bg-emerald-900\/30 {
  background-color: rgba(6, 78, 59, 0.3) !important;
}

.dark .text-sky-600 {
  color: #0284c7 !important;
}

.dark .text-sky-400 {
  color: #38bdf8 !important;
}

.dark .bg-sky-100 {
  background-color: rgba(2, 132, 199, 0.2) !important;
}

.dark .bg-sky-900\/30 {
  background-color: rgba(12, 74, 110, 0.3) !important;
}

.dark .text-cyan-600 {
  color: #0891b2 !important;
}

.dark .text-cyan-400 {
  color: #22d3ee !important;
}

.dark .bg-cyan-100 {
  background-color: rgba(8, 145, 178, 0.2) !important;
}

.dark .bg-cyan-900\/30 {
  background-color: rgba(22, 78, 99, 0.3) !important;
}

.dark .text-violet-600 {
  color: #7c3aed !important;
}

.dark .text-violet-400 {
  color: #a78bfa !important;
}

.dark .bg-violet-100 {
  background-color: rgba(124, 58, 237, 0.2) !important;
}

.dark .bg-violet-900\/30 {
  background-color: rgba(76, 29, 149, 0.3) !important;
}

.dark .text-amber-600 {
  color: #d97706 !important;
}

.dark .text-amber-400 {
  color: #fbbf24 !important;
}

.dark .bg-amber-100 {
  background-color: rgba(217, 119, 6, 0.2) !important;
}

.dark .bg-amber-900\/30 {
  background-color: rgba(120, 53, 15, 0.3) !important;
}

/* Focus states for form elements */
.dark input:focus,
.dark select:focus,
.dark textarea:focus {
  background-color: #475569 !important;
  border-color: #64748b !important;
  color: #f1f5f9 !important;
}

.dark input[type="color"]:focus {
  border-color: #10B981 !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
}

/* Placeholder text */
.dark input::placeholder,
.dark textarea::placeholder {
  color: #64748b !important;
}

/* Card Creation Modal Dark Mode */
.dark .bg-gradient-to-br {
  background: linear-gradient(to bottom right, #1e293b, #0f172a) !important;
}

.dark .bg-gradient-to-br.from-gray-50.to-gray-100 {
  background: linear-gradient(to bottom right, #1e293b, #0f172a) !important;
}

/* Field type buttons dark mode */
.dark .hover\:border-slate-400:hover {
  border-color: #64748b !important;
}

.dark .hover\:shadow-sm:hover {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2) !important;
}

/* Custom scrollbar for dark mode */
.dark ::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.dark ::-webkit-scrollbar-track {
  background: #1e293b;
  border-radius: 4px;
}

.dark ::-webkit-scrollbar-thumb {
  background: #475569;
  border-radius: 4px;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Dropdown and select elements */
.dark select option {
  background-color: #1e293b !important;
  color: #f1f5f9 !important;
}

/* Toggle switch dark mode */
.dark .relative input[type="checkbox"] {
  background-color: #475569 !important;
  border-color: #64748b !important;
}

.dark .relative input[type="checkbox"]:checked {
  background-color: #10B981 !important;
  border-color: #10B981 !important;
}

/* Specific overrides for modal backgrounds */
.dark .fixed.inset-0 .bg-white {
  background-color: #1e293b !important;
}

.dark .fixed.inset-0 .bg-gray-50 {
  background-color: #0f172a !important;
}

.dark .rounded-xl.shadow-2xl .bg-white {
  background-color: #1e293b !important;
}

/* Field options in select dropdown */
.dark .hover\:bg-orange-50:hover {
  background-color: rgba(249, 115, 22, 0.1) !important;
}

.dark .hover\:bg-orange-900\/20:hover {
  background-color: rgba(154, 52, 18, 0.2) !important;
}

.dark .text-orange-600 {
  color: #ea580c !important;
}

.dark .text-orange-400 {
  color: #fb923c !important;
}

.dark .border-orange-200 {
  border-color: rgba(249, 115, 22, 0.3) !important;
}

.dark .border-orange-800 {
  border-color: rgba(154, 52, 18, 0.8) !important;
}

.dark .hover\:border-orange-300:hover {
  border-color: rgba(249, 115, 22, 0.5) !important;
}

.dark .hover\:border-orange-700:hover {
  border-color: rgba(194, 65, 14, 0.7) !important;
}

/* Ensure proper text contrast in all elements */
.dark .text-slate-700 {
  color: #cbd5e1 !important;
}

.dark .text-slate-300 {
  color: #cbd5e1 !important;
}

/* Info boxes and alerts */
.dark .bg-blue-50 {
  background-color: rgba(59, 130, 246, 0.1) !important;
}

.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.3) !important;
}

.dark .border-blue-800 {
  border-color: rgba(30, 64, 175, 0.8) !important;
}

.dark .text-blue-900 {
  color: #bfdbfe !important;
}

.dark .text-blue-100 {
  color: #dbeafe !important;
}

.dark .text-blue-400 {
  color: #60a5fa !important;
}

/* Required field badges */
.dark .bg-red-100 {
  background-color: rgba(239, 68, 68, 0.2) !important;
}

.dark .text-red-800 {
  color: #fca5a5 !important;
}

/* Field preview elements */
.dark .bg-slate-700\/50 {
  background-color: rgba(51, 65, 85, 0.5) !important;
}

.dark .border-slate-600 {
  border-color: #475569 !important;
}

/* Ensure button text remains readable */
.dark button.bg-blue-100 {
  background-color: rgba(59, 130, 246, 0.2) !important;
  color: #60a5fa !important;
}

.dark button.bg-red-100 {
  background-color: rgba(239, 68, 68, 0.2) !important;
  color: #f87171 !important;
}

.dark button.hover\:bg-blue-200:hover {
  background-color: rgba(59, 130, 246, 0.3) !important;
}

.dark button.hover\:bg-red-200:hover {
  background-color: rgba(239, 68, 68, 0.3) !important;
}
</style> 

